import {
  Euler,
  Group,
  Matrix4,
  type OrthographicCamera,
  type PerspectiveCamera,
  Quaternion,
  TOUCH,
  Vector2,
  Vector3,
} from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI, { type Controller } from "lil-gui";

import { createScene } from "../scene";
import { Figure } from "../visual/Figure";
import { Dots } from "../visual/Dots";
import { GridDots, type GridDotsPaneConfig } from "../visual/GridDots";
import { KaleidoscopeRing } from "../visual/KaleidoscopeRing";
import { MetaballOverlay } from "../visual/MetaballOverlay";
import { Labels } from "../visual/Labels";
import { AxesGuide } from "../visual/AxesGuide";
import { isMobile } from "../util/mobile";
import {
  buildOctagonOnCube,
  OCTAGON_XI,
  type OctagonPreset,
} from "../data/octagonOnCube";
import pkg from "../../package.json";
import type { Logo, LogoShellContext } from "./Logo";
import {
  CanvasFrameCompositor,
  OfflineVideoEncoder,
  detectVideoFormat,
  downloadBlob,
  type VideoExt,
} from "./videoExporter";

type DisplayMode = "lines" | "both" | "dots" | "grid";

/**
 * 切頂立方体ビジュアライザを Logo インターフェースに包んだもの。
 *
 * 旧 main.ts のロジックをほぼそのまま mount() のローカルスコープに収め、
 * RAF ループの本体だけを updateFn として保持する。FPS 計測と RAF はシェルへ
 * 移管。canvas / label-layer / view-info / cube-hud / GUI はすべて mount で
 * 生成し、unmount で破棄する。
 */
export class CubeLogo implements Logo {
  readonly name = "cube";
  private updateFn: ((dt: number) => void) | null = null;
  private teardown: (() => void) | null = null;

  mount(container: HTMLElement, shell: LogoShellContext): void {
    // === 自前の DOM を container 内に構築 ===
    const canvas = document.createElement("canvas");
    canvas.id = "stage";
    container.appendChild(canvas);

    const labelLayer = document.createElement("div");
    labelLayer.id = "label-layer";
    container.appendChild(labelLayer);

    const viewInfoEl = document.createElement("div");
    viewInfoEl.id = "view-info";
    viewInfoEl.textContent = "azimuth — polar — zoom —";
    container.appendChild(viewInfoEl);

    const cubeHud = document.createElement("div");
    cubeHud.id = "cube-hud";
    cubeHud.innerHTML = `
      <span class="mode mobile-hide" id="mode">both</span>
      <button id="label-toggle" class="toggle mobile-hide" type="button" aria-pressed="false">labels: off</button>
      <button id="occlusion-toggle" class="toggle mobile-hide" type="button" aria-pressed="true">occlusion: on</button>
      <button id="random-view" class="toggle mobile-hide" type="button">random</button>
      <button id="reset-view" class="toggle mobile-hide" type="button">reset view</button>
      <button id="intro-play" class="toggle mobile-hide" type="button">intro</button>
      <button id="intro3-play" class="toggle mobile-hide" type="button">intro3</button>
      <button id="intro4-play" class="toggle mobile-hide" type="button">intro4</button>
      <button id="exp-webm" class="toggle mobile-hide" type="button">exp webm</button>
      <button id="exp-mp4" class="toggle mobile-hide" type="button">exp mp4</button>
      <button id="gui-toggle" class="toggle mobile-hide" type="button" title="GUI 表示/非表示">⚙</button>
      <span id="export-status" class="export-status mobile-hide"></span>
      <span class="hint mobile-hide">1/2/3/4: mode / M: cycle / L: labels / O: occlusion / N: random / R: reset / SPACE: motion / drag: rotate / wheel: zoom</span>
    `;
    container.appendChild(cubeHud);

    // === モバイル専用 UI ===
    // 上部: ロゴ説明テキスト (2 行)
    // 下部: スタート / 元に戻す のピル型ボタン
    // すべて mobile-only クラスで PC では非表示。
    const mobileIntroText = document.createElement("div");
    mobileIntroText.id = "mobile-intro-text";
    mobileIntroText.className = "mobile-only";
    mobileIntroText.innerHTML =
      "こちらのロゴマークは<br>指で動かせるようになっています";
    container.appendChild(mobileIntroText);

    const mobileControls = document.createElement("div");
    mobileControls.id = "mobile-controls";
    mobileControls.className = "mobile-only";
    mobileControls.innerHTML = `
      <button id="mobile-start" class="pill-btn" type="button">スタート</button>
      <button id="mobile-reset" class="pill-btn" type="button">元に戻す</button>
    `;
    container.appendChild(mobileControls);

    const modeEl = cubeHud.querySelector<HTMLSpanElement>("#mode");
    const labelToggleBtn =
      cubeHud.querySelector<HTMLButtonElement>("#label-toggle");
    const occlusionToggleBtn =
      cubeHud.querySelector<HTMLButtonElement>("#occlusion-toggle");
    const resetBtn = cubeHud.querySelector<HTMLButtonElement>("#reset-view");
    const randomBtn = cubeHud.querySelector<HTMLButtonElement>("#random-view");
    const expWebmBtn = cubeHud.querySelector<HTMLButtonElement>("#exp-webm");
    const expMp4Btn = cubeHud.querySelector<HTMLButtonElement>("#exp-mp4");
    const introBtn = cubeHud.querySelector<HTMLButtonElement>("#intro-play");
    const intro3Btn = cubeHud.querySelector<HTMLButtonElement>("#intro3-play");
    const intro4Btn = cubeHud.querySelector<HTMLButtonElement>("#intro4-play");
    const guiToggleBtn = cubeHud.querySelector<HTMLButtonElement>("#gui-toggle");
    const mobileStartBtn =
      mobileControls.querySelector<HTMLButtonElement>("#mobile-start");
    const mobileResetBtn =
      mobileControls.querySelector<HTMLButtonElement>("#mobile-reset");

    // === scene / renderer ===
    const sceneCtx = createScene(canvas);
    const { renderer, scene } = sceneCtx;
    // camera は projection 切替で差し替えるので let。以降の参照はこの変数を見る。
    let camera: OrthographicCamera | PerspectiveCamera = sceneCtx.camera;

    const labelRenderer = new CSS2DRenderer({ element: labelLayer });
    const syncRenderersSize = (): void => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      labelRenderer.setSize(w, h);
      figure.setResolution(w, h);
      kaleidoscope.setResolution(w, h);
      // metaball は gl_FragCoord 物理ピクセル基準なので drawing buffer サイズ。
      const db = renderer.getDrawingBufferSize(_metaballDbSize);
      metaball.setResolution(db.x, db.y);
    };

    const figureData = buildOctagonOnCube();

    const root = new Group();
    scene.add(root);

    const figure = new Figure(figureData);
    root.add(figure.group);

    const dots = new Dots(figureData);
    root.add(dots.group);

    const gridDots = new GridDots(figureData, labelLayer);

    const labels = new Labels(figureData);
    root.add(labels.group);

    // フェード速度 (per second)。1 / FADE_RATE 秒で 0↔1 を完全遷移。
    // 4 = 250ms。視点が変わったときの「裏に回ったから消える」を soft にする。
    const FADE_RATE = 4;
    const dotOpacities: number[] = new Array(dots.count).fill(1);
    // per-vertex の最終 opacity (occlusion × mode) を毎フレーム貯めて、
    // 万華鏡コピーのドットへ流用する (全コピーは同じ回転 = 同じ可視性)。
    const dotFinalOpacities: number[] = new Array(dots.count).fill(1);

    const axesGuide = new AxesGuide();
    scene.add(axesGuide.group);

    // 万華鏡リング: メイン cube の周囲に同期回転コピーを N 個並べる。
    // ring は世界座標系 (root の子ではない) に配置し、毎フレーム view 軸に
    // 垂直な平面で再配置する。各コピーの quaternion は root.quaternion を
    // コピーして完全同期。
    const kaleidoscope = new KaleidoscopeRing(figureData);
    scene.add(kaleidoscope.group);

    // メタボール・オーバーレイ: 24 頂点を画面空間メタボールとして描画する
    // フルスクリーンシェーダー。enabled のときだけ毎フレーム頂点を投影して描画。
    const metaball = new MetaballOverlay();
    // drawing buffer サイズ / 頂点投影に使う再利用バッファ。
    const _metaballDbSize = new Vector2();
    const _metaballProj = new Vector3();
    // occlusion × heartbeat を合成した metaball 用 per-vertex 重み。
    const _metaballWeights = new Float32Array(figureData.vertices.length);

    // === intro アニメーション (1 blob → 8 リング → 24 切頂立方体) ===
    // 中央のメタボール 1 個から始まり、8 個のリング (オクタゴン状) に分裂、
    // さらに 8 → 24 (各 cube 角に 3 個ずつ) に広がって本体の形へ収束する。
    // メタボールオーバーレイを駆動するための位置だけを intro 中に差し替え、
    // 通常のドット/ラインは intro 中 root.visible=false で隠す。
    const INTRO_DURATION = 3.0;
    // リング半径は「8 つのメタボール blob が field 上で明確に分離する」サイズに
    // 取る。0.6 だと midpoint の field が threshold を割らず連結して見える。
    const INTRO_RING_R = 0.9;
    const introVertices: [number, number, number][] = figureData.vertices.map(
      () => [0, 0, 0],
    );
    // リング位置は「世界 XY 平面」(= 画面平面) 上の正八角形。各頂点は root の
    // 子なので、毎フレーム root の回転の逆を当てた local 座標を求め、画面上で
    // 常に正八角形に見えるようにする (cube がどんな向きでもリングは平らな八角形)。
    const ringWorld: [number, number, number][] = [];
    for (let k = 0; k < 8; k++) {
      // 22.5° オフセットでカーディナル (上下左右真ん中) を避ける配置。
      // 各辺 (上下左右) に 2 ドットずつが対称に並ぶ正八角形になる。
      const theta = (k / 8) * Math.PI * 2 + Math.PI / 8;
      ringWorld.push([
        INTRO_RING_R * Math.cos(theta),
        INTRO_RING_R * Math.sin(theta),
        0,
      ]);
    }
    const ringPos: [number, number, number][] = ringWorld.map(() => [0, 0, 0]);
    const _introInvQuat = new Quaternion();
    const _introRingScratch = new Vector3();
    const updateRingLocal = (): void => {
      _introInvQuat.copy(root.quaternion).invert();
      for (let k = 0; k < 8; k++) {
        _introRingScratch
          .set(ringWorld[k][0], ringWorld[k][1], ringWorld[k][2])
          .applyQuaternion(_introInvQuat);
        ringPos[k][0] = _introRingScratch.x;
        ringPos[k][1] = _introRingScratch.y;
        ringPos[k][2] = _introRingScratch.z;
      }
    };
    // 各頂点を 8 リング位置のうち「最も近い」位置に割り当てる。intro 開始時の
    // cube 向きで投影 → 画面角度 → 8 リング (45° 刻み) のうち最近接のものを選ぶ。
    // これにより 8 → 24 の遷移で各頂点が「すぐ近くのリング点から分裂」する自然な
    // 動きになる (遠回りな縦横移動が出ない)。intro 中は固定して使う。
    const vertexRingIdx: number[] = figureData.vertices.map(() => 0);
    // intro 全期間を通して cube に追加スピンをかける (画面 view 軸 = world Z)。
    // 正値 = 左 (反時計回り) / 負値 = 右 (時計回り)。t=0 で INTRO_SPIN_ANGLE、
    // t=1 で 0 へ単調 ease-in-out。phase 0/1/hold は ring が画面 XY に張り付くため
    // 回転は不可視、phase 2 で dots が cube 位置へ動き始めた時点から見え始める。
    const INTRO_SPIN_ANGLE = Math.PI * 2; // +360° = 左 (反時計回り) に1回転して収束
    const _introZAxis = new Vector3(0, 0, 1);
    const _introSpinQuat = new Quaternion();
    const computeNearestRingAssignment = (): void => {
      root.updateMatrixWorld();
      const wp = new Vector3();
      const ringAngles: number[] = [];
      for (let k = 0; k < 8; k++) {
        ringAngles.push((k / 8) * Math.PI * 2 + Math.PI / 8);
      }
      for (let i = 0; i < figureData.vertices.length; i++) {
        const [vx, vy, vz] = figureData.vertices[i];
        wp.set(vx, vy, vz).applyMatrix4(root.matrixWorld);
        const a = Math.atan2(wp.y, wp.x);
        let best = 0;
        let bestDist = Infinity;
        for (let k = 0; k < 8; k++) {
          let d = a - ringAngles[k];
          while (d > Math.PI) d -= Math.PI * 2;
          while (d < -Math.PI) d += Math.PI * 2;
          const ad = Math.abs(d);
          if (ad < bestDist) {
            bestDist = ad;
            best = k;
          }
        }
        vertexRingIdx[i] = best;
      }
    };
    let introActive = false;
    let introTimer = 0;
    // タイムライン用: introPlaying = true で自動進行、false でその位置にホールド
    // (スライダーで scrub したときは false に落ちて任意の時刻で確認できる)。
    let introPlaying = false;
    // GUI の time スライダー (auto-play 中も timer に同期させる)。
    let introTimeCtrl: Controller | null = null;
    // フェーズ境界 (t∈[0,1] の正規化進行):
    //   [0.00, INTRO_PHASE_1_START) 単一ドット (大きめ)
    //   [INTRO_PHASE_1_START, INTRO_PHASE_1_END) 1 → 8 (リング展開)
    //   [INTRO_PHASE_1_END, INTRO_HOLD_END) 8 オクタゴンでホールド
    //   [INTRO_HOLD_END, 1.00) 8 → 24 (cube 展開)
    const INTRO_PHASE_1_START = 0.1;
    const INTRO_PHASE_1_END = 0.45;
    const INTRO_HOLD_END = 0.6;
    // 単一ドット時のサイズ倍率 (= 通常サイズの何倍にするか)。
    const INTRO_BIG_SIZE_MULT = 1.8;
    const introEaseInOut = (t: number): number =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const computeIntroVertices = (t: number): void => {
      // 毎フレーム root の現回転に応じた local リング座標を更新 (画面上の正八角形を維持)。
      updateRingLocal();
      for (let i = 0; i < introVertices.length; i++) {
        const target = figureData.vertices[i];
        const ring = ringPos[vertexRingIdx[i]];
        const v = introVertices[i];
        if (t < INTRO_PHASE_1_START) {
          v[0] = 0;
          v[1] = 0;
          v[2] = 0;
        } else if (t < INTRO_PHASE_1_END) {
          const u = introEaseInOut(
            (t - INTRO_PHASE_1_START) /
              (INTRO_PHASE_1_END - INTRO_PHASE_1_START),
          );
          v[0] = ring[0] * u;
          v[1] = ring[1] * u;
          v[2] = ring[2] * u;
        } else if (t < INTRO_HOLD_END) {
          v[0] = ring[0];
          v[1] = ring[1];
          v[2] = ring[2];
        } else {
          // phase 2: リング → 各 cube 頂点 (local 直線補間)。cube の追加回転は
          // 上の intro tick 側で root.quaternion をスピンさせて反映する。
          const u = introEaseInOut(
            (t - INTRO_HOLD_END) / (1 - INTRO_HOLD_END),
          );
          v[0] = ring[0] + (target[0] - ring[0]) * u;
          v[1] = ring[1] + (target[1] - ring[1]) * u;
          v[2] = ring[2] + (target[2] - ring[2]) * u;
        }
      }
    };
    const startIntro = (): void => {
      // 現在の cube 向きで各頂点 → 最近接リングの割当てを計算してロック。
      // intro3/4 が動いていたら停止 (相互排他)。
      intro3Active = false;
      intro3Playing = false;
      intro4Active = false;
      intro4Playing = false;
      computeNearestRingAssignment();
      introActive = true;
      introTimer = 0;
      introPlaying = true;
    };
    // スクラブ: 任意の時刻でホールド (introPlaying=false)。intro 未開始時もここで
    // 入り、最近接リング割当てを現在の cube 向きで計算する。
    const scrubIntro = (norm: number): void => {
      intro3Active = false;
      intro3Playing = false;
      intro4Active = false;
      intro4Playing = false;
      if (!introActive) computeNearestRingAssignment();
      introActive = true;
      introPlaying = false;
      introTimer = Math.max(0, Math.min(1, norm)) * INTRO_DURATION;
    };
    // intro モードを完全に抜けて通常 cube 表示へ戻す (ライン fade-in 込み)。
    const exitIntro = (): void => {
      introActive = false;
      introPlaying = false;
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      root.quaternion.copy(INITIAL_QUAT);
      linesIntroFadeT = 0;
    };
    const endIntro = (): void => {
      introActive = false;
      // ドット位置とサイズを本来の値へ戻す。figure は次フレームの通常ロジックが
      // 触らないので明示的に再表示。labels/kaleidoscope は params 経由で復元。
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      // ライン (とコピーのライン) のフェードイン開始。
      linesIntroFadeT = 0;
    };

    // === intro3 共有データ (cube 構造から事前計算する鏡映パートナー / 所属面 /
    //     phase 1/2a の格納先 index)。intro3 の computeIntro3Vertices が参照する。
    // 各 cube 頂点の鏡映パートナー:
    //   intro3MirrorAB[i]: x=y 平面で鏡映した相手 (3D で A 面 ↔ B 面 を交換)
    //                      例: A-only (1, XI, -1) ↔ B-only (XI, 1, -1)
    //   intro3MirrorBC[i]: y=z 平面で鏡映した相手 (3D で B 面 ↔ C 面 を交換)
    //                      例: B-only (XI, 1, -1) ↔ C-only (XI, -1, 1)
    // 2D の cube 投影上では、AB 軸鏡映 / BC 軸鏡映に対応する。
    const findMirror = (i: number, ax: number, bx: number): number => {
      const v = figureData.vertices[i];
      const target = [v[0], v[1], v[2]];
      const t = target[ax];
      target[ax] = target[bx];
      target[bx] = t;
      for (let j = 0; j < figureData.vertices.length; j++) {
        const u = figureData.vertices[j];
        if (
          Math.abs(u[0] - target[0]) < 0.01 &&
          Math.abs(u[1] - target[1]) < 0.01 &&
          Math.abs(u[2] - target[2]) < 0.01
        ) {
          return j;
        }
      }
      return i;
    };
    const intro3MirrorAB: number[] = figureData.vertices.map((_, i) =>
      findMirror(i, 0, 1),
    );
    const intro3MirrorBC: number[] = figureData.vertices.map((_, i) =>
      findMirror(i, 1, 2),
    );
    // 各 cube 頂点の所属面 (cube ローカル座標、|XI| < 0.5 < 1):
    const intro3OnA: boolean[] = figureData.vertices.map((v) => v[0] > 0.5);
    const intro3OnB: boolean[] = figureData.vertices.map((v) => v[1] > 0.5);
    const intro3OnC: boolean[] = figureData.vertices.map((v) => v[2] > 0.5);
    // 各頂点の phase 1 終了時の格納先 (折り重なり) と phase 2a 終了時の位置:
    //   A 面 (= x=1, 8 頂点): 自身の位置に展開
    //   B 面 (NOT A、6 頂点): 鏡映パートナー (A 面側) に折り重なる
    //   C only (4 頂点): B 面パートナー (= y=z 鏡映) → さらに A 面パートナー
    //                    (= x=y 鏡映) で二重折り重なり
    //   裏面 (6 頂点): 自身の位置 (オクルージョン非表示)
    const intro3Phase1End: number[] = figureData.vertices.map((_, i) => {
      if (intro3OnA[i]) return i;
      if (intro3OnB[i]) return intro3MirrorAB[i];
      if (intro3OnC[i]) return intro3MirrorAB[intro3MirrorBC[i]];
      return i;
    });
    // Phase 2a 終了時: A/B 面は自身の位置、C only は B 面パートナーに折り重なる。
    const intro3Phase2aEnd: number[] = figureData.vertices.map((_, i) => {
      if (intro3OnA[i] || intro3OnB[i]) return i;
      if (intro3OnC[i]) return intro3MirrorBC[i];
      return i;
    });

    // === intro3 (cube 投影座標 + 鏡映パートナー + ghost 反射子による 6 点鏡開き)
    //   phase 0/1/hold: 中心 → A 面 (= cube 投影上の 8 位置に 24 頂点が折り重なる)
    //   phase 2a: 鏡開き #1 (AB 軸鏡映、A → A+B、6 dots 展開)
    //   phase 2b: 鏡開き #2 (BC 軸鏡映、B → C、6 dots 展開、うち 2 つは ghost 役)
    let intro3Active = false;
    let intro3Timer = 0;
    let intro3Playing = false;
    let intro3TimeCtrl: Controller | null = null;
    // intro3 時間配分: 単一 0.3s / 1→8 0.9s / hold 0.3s / 2a 1.0s / 2b 1.0s = total 3.5s
    const INTRO3_DURATION = 3.5;
    const INTRO3_PHASE_1_START = 0.3 / 3.5; // ≈ 0.0857
    const INTRO3_PHASE_1_END = 1.2 / 3.5; // ≈ 0.3429
    const INTRO3_HOLD_END = 1.5 / 3.5; // ≈ 0.4286
    const INTRO3_MID = 2.5 / 3.5; // ≈ 0.7143 (2a = 1.0s、2b = 1.0s)
    // 各 cube 頂点の鏡映パートナー / 所属面 / 中継 index は前方で事前計算済み
    // (intro3MirrorAB / intro3MirrorBC / intro3OnA/B/C / intro3Phase1End / Phase2aEnd)。
    const intro3IsBack: boolean[] = figureData.vertices.map(() => false);
    // 6 点鏡開きの為の「ghost 反射子」設定: 2 つの裏面頂点を AB shared → AC shared
    // へ反射させる役割に割当てる。これで BC 軸鏡映が「軸 2 点を除く 6 点」になる。
    //   - 4 C-only は B-only → 自身の C-only 位置へ (見える 4 dots)
    //   - 2 ghost は AB shared → AC shared 位置へ (intro3 中だけ可視化)
    let intro3GhostAtoCFrom: number[] = []; // ghost の phase 2a 終端 (AB shared index)
    let intro3GhostAtoCTo: number[] = []; // ghost の phase 2b 終端 (AC shared index)
    const intro3IsGhost: boolean[] = figureData.vertices.map(() => false);
    const intro3GhostFrom: number[] = figureData.vertices.map(() => -1);
    const intro3GhostTo: number[] = figureData.vertices.map(() => -1);
    {
      // AB shared 2 個と AC shared 2 個の index を特定 (XI 値で正負を区別)
      let abPos = -1,
        abNeg = -1,
        acPos = -1,
        acNeg = -1;
      for (let i = 0; i < figureData.vertices.length; i++) {
        const v = figureData.vertices[i];
        if (v[0] > 0.5 && v[1] > 0.5 && Math.abs(v[2]) < 0.5) {
          if (v[2] > 0) abPos = i;
          else abNeg = i;
        } else if (v[0] > 0.5 && Math.abs(v[1]) < 0.5 && v[2] > 0.5) {
          if (v[1] > 0) acPos = i;
          else acNeg = i;
        }
      }
      // 裏面頂点を 2 つ選んで ghost 役割を割当
      const backVerts: number[] = [];
      for (let i = 0; i < figureData.vertices.length; i++) {
        const v = figureData.vertices[i];
        if (v[0] <= 0.5 && v[1] <= 0.5 && v[2] <= 0.5) {
          backVerts.push(i);
        }
      }
      if (
        backVerts.length >= 2 &&
        abPos >= 0 &&
        abNeg >= 0 &&
        acPos >= 0 &&
        acNeg >= 0
      ) {
        intro3IsGhost[backVerts[0]] = true;
        intro3GhostFrom[backVerts[0]] = abPos;
        intro3GhostTo[backVerts[0]] = acPos;
        intro3IsGhost[backVerts[1]] = true;
        intro3GhostFrom[backVerts[1]] = abNeg;
        intro3GhostTo[backVerts[1]] = acNeg;
        intro3GhostAtoCFrom = [abPos, abNeg];
        intro3GhostAtoCTo = [acPos, acNeg];
      }
    }
    void intro3GhostAtoCFrom;
    void intro3GhostAtoCTo;
    const computeIntro3BackCache = (): void => {
      camera.getWorldPosition(_cameraWorld);
      _rotMat.extractRotation(root.matrixWorld);
      const faceNormals = figureData.vertexFaceNormals;
      for (let i = 0; i < figureData.vertices.length; i++) {
        const [vx, vy, vz] = figureData.vertices[i];
        _vertWorld.set(vx, vy, vz).applyMatrix4(root.matrixWorld);
        _toCamera.subVectors(_cameraWorld, _vertWorld).normalize();
        const fs = faceNormals[i];
        let visible = false;
        for (let f = 0; f < 3; f++) {
          const n = fs[f];
          _normalWorld.set(n[0], n[1], n[2]).applyMatrix4(_rotMat);
          if (_toCamera.dot(_normalWorld) > -FACE_VISIBLE_EPSILON) {
            visible = true;
            break;
          }
        }
        intro3IsBack[i] = !visible;
      }
    };
    const computeIntro3Assignment = (): void => {
      root.updateMatrixWorld();
      computeIntro3BackCache();
    };
    const computeIntro3Vertices = (t: number): void => {
      for (let i = 0; i < introVertices.length; i++) {
        // Ghost 頂点 (= 6 点鏡開きの「AB → AC」反射子) は通常の Phase1End/Phase2aEnd
        // とは別の target を使う:
        //   phase 1 終端: AB shared 位置 (= ghostFrom)
        //   phase 2a 終端: AB shared 位置 (動かない)
        //   phase 2b 終端: AC shared 位置 (= ghostTo)
        let target: [number, number, number];
        let p1End: [number, number, number];
        let p2aEnd: [number, number, number];
        if (intro3IsGhost[i]) {
          target = figureData.vertices[intro3GhostTo[i]];
          p1End = figureData.vertices[intro3GhostFrom[i]];
          p2aEnd = figureData.vertices[intro3GhostFrom[i]];
        } else {
          target = figureData.vertices[i];
          p1End = figureData.vertices[intro3Phase1End[i]];
          p2aEnd = figureData.vertices[intro3Phase2aEnd[i]];
        }
        const v = introVertices[i];
        if (t < INTRO3_PHASE_1_START) {
          v[0] = 0;
          v[1] = 0;
          v[2] = 0;
        } else if (t < INTRO3_PHASE_1_END) {
          const u = introEaseInOut(
            (t - INTRO3_PHASE_1_START) /
              (INTRO3_PHASE_1_END - INTRO3_PHASE_1_START),
          );
          v[0] = p1End[0] * u;
          v[1] = p1End[1] * u;
          v[2] = p1End[2] * u;
        } else if (t < INTRO3_HOLD_END) {
          v[0] = p1End[0];
          v[1] = p1End[1];
          v[2] = p1End[2];
        } else if (t < INTRO3_MID) {
          const u = introEaseInOut(
            (t - INTRO3_HOLD_END) / (INTRO3_MID - INTRO3_HOLD_END),
          );
          v[0] = p1End[0] + (p2aEnd[0] - p1End[0]) * u;
          v[1] = p1End[1] + (p2aEnd[1] - p1End[1]) * u;
          v[2] = p1End[2] + (p2aEnd[2] - p1End[2]) * u;
        } else {
          const u = introEaseInOut((t - INTRO3_MID) / (1 - INTRO3_MID));
          v[0] = p2aEnd[0] + (target[0] - p2aEnd[0]) * u;
          v[1] = p2aEnd[1] + (target[1] - p2aEnd[1]) * u;
          v[2] = p2aEnd[2] + (target[2] - p2aEnd[2]) * u;
        }
      }
    };
    const startIntro3 = (): void => {
      introActive = false;
      introPlaying = false;
      intro4Active = false;
      intro4Playing = false;
      root.quaternion.copy(INITIAL_QUAT);
      root.updateMatrixWorld();
      computeIntro3Assignment();
      intro3Active = true;
      intro3Timer = 0;
      intro3Playing = true;
    };
    const scrubIntro3 = (norm: number): void => {
      introActive = false;
      introPlaying = false;
      intro4Active = false;
      intro4Playing = false;
      if (!intro3Active) {
        root.quaternion.copy(INITIAL_QUAT);
        root.updateMatrixWorld();
        computeIntro3Assignment();
      }
      intro3Active = true;
      intro3Playing = false;
      intro3Timer = Math.max(0, Math.min(1, norm)) * INTRO3_DURATION;
    };
    // ghost 頂点 (intro3 中強制可視化) を非表示に戻す。intro3 終了直後に
    // 通常 occlusion ロジックが走るまでの 1 フレームのラグで「ghost 頂点が裏面
    // 位置で一瞬可視化される」フリックを防ぐ。
    const hideIntro3Ghosts = (): void => {
      for (let i = 0; i < dotOpacities.length; i++) {
        if (intro3IsGhost[i]) {
          dotOpacities[i] = 0;
          dotFinalOpacities[i] = 0;
          dots.setOpacity(i, 0);
          const child = dots.group.children[i];
          if (child) child.visible = false;
        }
      }
    };
    const exitIntro3 = (): void => {
      intro3Active = false;
      intro3Playing = false;
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      root.quaternion.copy(INITIAL_QUAT);
      linesIntroFadeT = 0;
      hideIntro3Ghosts();
    };
    const endIntro3 = (): void => {
      intro3Active = false;
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      linesIntroFadeT = 0;
      hideIntro3Ghosts();
    };

    // === intro4 (1→8 ring → face-on cube (12 dots) → rotate to body-diagonal)
    //   phase 0: 0..INTRO4_PHASE_1_START 単一 dot
    //   phase 1: ..PHASE_1_END  1→8 中央寄せ ring (root.q = identity、world XY)
    //   hold ring: ..HOLD_RING_END
    //   phase 2: ..FACE_ON_END  8 ring → 12 dots face-on cube
    //   hold face-on: ..HOLD_FACE_ON_END  cube が face-on で 12 dots 静止
    //   phase 3: ..ROTATE_END  root.q slerp identity → INITIAL_QUAT (cube 回転)
    //   final hold: ..1.0  body-diagonal 表示 (= image 30)
    // total = 4.0s
    let intro4Active = false;
    let intro4Timer = 0;
    let intro4Playing = false;
    let intro4TimeCtrl: Controller | null = null;
    // 配分 (秒): 単一 0.2 / 1→8 1.0 / hold 0.2 / phase 2 0.4 / hold face-on 1.2
    //          / rotate 0.8 (0.75 倍速) / final hold 0.4
    // total = 4.2s
    const INTRO4_DURATION = 4.2;
    const INTRO4_PHASE_1_START = 0.2 / 4.2;
    const INTRO4_PHASE_1_END = 1.2 / 4.2;
    const INTRO4_HOLD_RING_END = 1.4 / 4.2;
    const INTRO4_FACE_ON_END = 1.8 / 4.2;
    const INTRO4_HOLD_FACE_ON_END = 3.0 / 4.2;
    const INTRO4_ROTATE_END = 3.8 / 4.2;
    // 8→12 fade in スキップ中の未使用抑止 (fade in 再有効化時に参照される)
    void INTRO4_HOLD_RING_END;
    void INTRO4_FACE_ON_END;
    // 8 つの "corner" 頂点 (|x|=|y|=1, |z|=XI) は face-on で 4 unique 2D 位置 (±1, ±1)
    // に投影される。これらは phase 2 で「その場でフェードイン」する 4 dots。
    // それ以外 16 頂点は ring → cube vertex local へ morph (= ring の 8 角と同じ角度
    // で edge 位置に移動)。
    const intro4IsCorner: boolean[] = figureData.vertices.map(
      (v) =>
        Math.abs(v[0]) > 0.5 &&
        Math.abs(v[1]) > 0.5 &&
        Math.abs(v[2]) < 0.5,
    );
    const computeIntro4Vertices = (t: number): void => {
      for (let i = 0; i < introVertices.length; i++) {
        const target = figureData.vertices[i]; // cube vertex local position
        const v = introVertices[i];
        // Corner 頂点 (8 個): 常に cube corner 位置で固定。phase 2 で opacity fade in。
        if (intro4IsCorner[i]) {
          v[0] = target[0];
          v[1] = target[1];
          v[2] = target[2];
          continue;
        }
        // 非 corner 頂点 (16 個): phase 1 で原点 → cube edge 位置へ展開。
        //   face-on view (root.q = identity) で cube edge 位置 = 正八角形 (8 unique
        //   2D 位置、2 cube 頂点が各位置に重なる)。これで「1→8 中央寄せ ring」と
        //   同じ視覚効果 (ただしサイズが cube edge スケール、radius ≈ 0.887)。
        //   phase 2 以降は cube edge 位置で固定 (動かない、形変化なし)。
        if (t < INTRO4_PHASE_1_START) {
          v[0] = 0;
          v[1] = 0;
          v[2] = 0;
        } else if (t < INTRO4_PHASE_1_END) {
          const u = introEaseInOut(
            (t - INTRO4_PHASE_1_START) /
              (INTRO4_PHASE_1_END - INTRO4_PHASE_1_START),
          );
          v[0] = target[0] * u;
          v[1] = target[1] * u;
          v[2] = target[2] * u;
        } else {
          // hold ring / phase 2 (corner fade) / hold face-on / rotate / final hold:
          // 非 corner は cube edge 位置で固定。
          v[0] = target[0];
          v[1] = target[1];
          v[2] = target[2];
        }
      }
    };
    const startIntro4 = (): void => {
      // intro4 中も _tbVel が走ると終了直後に cube がスピンしてしまうのでクリア。
      _tbVel.set(0, 0, 0);
      controlsActive = false;
      introActive = false;
      introPlaying = false;
      intro3Active = false;
      intro3Playing = false;
      // intro4 では root.q = identity (face-on) を基準。ring assignment も
      // face-on projection の角度で計算する。
      root.quaternion.identity();
      root.updateMatrixWorld();
      computeNearestRingAssignment();
      intro4Active = true;
      intro4Timer = 0;
      intro4Playing = true;
    };
    const scrubIntro4 = (norm: number): void => {
      introActive = false;
      introPlaying = false;
      intro3Active = false;
      intro3Playing = false;
      if (!intro4Active) {
        root.quaternion.identity();
        root.updateMatrixWorld();
        computeNearestRingAssignment();
      }
      intro4Active = true;
      intro4Playing = false;
      intro4Timer = Math.max(0, Math.min(1, norm)) * INTRO4_DURATION;
    };
    const exitIntro4 = (): void => {
      intro4Active = false;
      intro4Playing = false;
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      root.quaternion.copy(INITIAL_QUAT);
      linesIntroFadeT = 0;
    };
    const endIntro4 = (): void => {
      intro4Active = false;
      dots.setPositions(figureData.vertices);
      dots.setSize(pointSizeCurrent);
      figure.group.visible = true;
      linesIntroFadeT = 0;
    };

    syncRenderersSize();
    window.addEventListener("resize", syncRenderersSize);

    // 「見え方全体を 180° 回転」= world Z (視線軸) まわりに 180° 回した状態を
    // 初期視点とする。three.js の Euler "XYZ" 順では Z は innermost (= local Z
    // 適用) なので Z 値に π を加えても効かない。quaternion を post-multiply
    // (matrix 表記での pre-multiply = world 軸まわりの追加回転) で実装する。
    const _initialBaseQuat = new Quaternion().setFromEuler(
      new Euler(Math.atan(1 / Math.SQRT2), -Math.PI / 4, 0, "XYZ"),
    );
    const _initialFlipQuat = new Quaternion().setFromAxisAngle(
      new Vector3(0, 0, 1),
      Math.PI,
    );
    const INITIAL_QUAT = new Quaternion().multiplyQuaternions(
      _initialFlipQuat,
      _initialBaseQuat,
    );

    root.quaternion.copy(INITIAL_QUAT);

    const _initialEuler = new Euler().setFromQuaternion(INITIAL_QUAT, "XYZ");
    const INITIAL_ROT_X_DEG = (_initialEuler.x * 180) / Math.PI;
    const INITIAL_ROT_Y_DEG = (_initialEuler.y * 180) / Math.PI;
    const INITIAL_ROT_Z_DEG = (_initialEuler.z * 180) / Math.PI;

    let rotateXTarget = INITIAL_ROT_X_DEG;
    let rotateXCurrent = INITIAL_ROT_X_DEG;
    let rotateYTarget = INITIAL_ROT_Y_DEG;
    let rotateYCurrent = INITIAL_ROT_Y_DEG;
    let rotateZTarget = INITIAL_ROT_Z_DEG;
    let rotateZCurrent = INITIAL_ROT_Z_DEG;

    // PC のデフォルト zoom はブランドモック (= 0.5) に合わせる。
    const baseZoom = 0.5;
    // モバイルでは画面が狭いので cube を縮小。MOBILE_SCALE は「PC 比」だが、
    // 旧 baseZoom (1/1.5) 時代の mobile cube radius を維持するため、
    // 0.396 × (0.667/0.5) = 0.528 にスケーリング。
    const MOBILE_SCALE = 0.528;
    const initialZoom = isMobile() ? baseZoom * MOBILE_SCALE : baseZoom;
    camera.zoom = initialZoom;
    camera.updateProjectionMatrix();
    sceneCtx.perspectiveCamera.zoom = initialZoom;
    sceneCtx.perspectiveCamera.updateProjectionMatrix();

    // 投影タイプ。ortho/perspective を GUI で切り替える。zoom clamp の扱いが
    // 違うので configureControls 内で分岐する。
    let projectionType: "ortho" | "perspective" = "ortho";

    // OrbitControls の共通設定 + 操作開始/終了リスナーを付与する。projection
    // 切替で controls を作り直すたびに呼ぶ。
    function configureControls(c: OrbitControls): void {
      c.enableDamping = true;
      c.dampingFactor = 0.08;
      c.enablePan = false;
      c.rotateSpeed = 0.7;
      c.target.set(0, 0, 0);
      // モバイルは「指で回す」だけのデモ。OrbitControls は球面座標 (azimuth/polar)
      // でカメラを回すため、polar が 0 / π に達すると gimbal lock で縦に回せなく
      // なる。これを避けるため、モバイルでは OrbitControls の rotate / zoom を無効化
      // し、代わりに canvas に独自タッチハンドラを付けて root.quaternion を直接
      // trackball 風に回す (下の registerTrackballTouch を参照)。
      if (isMobile()) {
        c.enableRotate = false;
        c.enableZoom = false;
        c.touches.ONE = TOUCH.ROTATE;
        c.touches.TWO = TOUCH.ROTATE;
      }
      if (projectionType === "ortho") {
        // 正射影はホイールで camera.zoom を変える。breathing もこの範囲に丸める。
        // baseZoom 変更時にも自動追従するよう、clamp は baseZoom 比 0.75〜1.125
        // (= breathing ±7% に十分な余白) で定義。モバイルは MOBILE_SCALE で更に縮小。
        const cMin = baseZoom * 0.75;
        const cMax = baseZoom * 1.125;
        if (isMobile()) {
          c.minZoom = cMin * MOBILE_SCALE;
          c.maxZoom = cMax * MOBILE_SCALE;
        } else {
          c.minZoom = cMin;
          c.maxZoom = cMax;
        }
      } else {
        // 透視はホイールで dolly (距離)。zoom は breathing 用に保持。
        c.minDistance = 3.5;
        c.maxDistance = 7;
      }
      // ユーザーが直接視点操作を始めたら、その間と直後数秒は自動アニメを抑止。
      c.addEventListener("start", () => {
        controlsActive = true;
        scheduleNextAutoTrigger(nowSec() + 4);
      });
      c.addEventListener("end", () => {
        controlsActive = false;
        scheduleNextAutoTrigger(nowSec() + 4);
        breathingBaseZoom = camera.zoom;
        breathingPhase = 0;
      });
    }

    let controls = new OrbitControls(camera, renderer.domElement);
    configureControls(controls);

    // モバイル用の trackball タッチ回転。OrbitControls の rotate は無効化済み。
    // 仕組み: drag (dx, dy) を camera-local の「画面平面内で drag に垂直な軸」
    // (= (dy, dx, 0) を normalize したもの) を world 座標に変換し、その軸まわりに
    // |drag| に比例した角度で root.quaternion を premultiply する。
    // 球面座標ではなく直接四元数を回すので gimbal lock が発生しない。
    //
    // 慣性: 指を離した後も _tbVel (world 系の角速度ベクトル) で root を回し続ける。
    // _tbVel は drag 中の瞬時角速度を EMA で平滑化したもの。touchend 後は
    // updateFn 内で _DECAY に従い指数減衰し、|_tbVel| が閾値を下回るまで動き続ける。
    let _tbDragging = false;
    let _tbLastX = 0;
    let _tbLastY = 0;
    let _tbLastMoveMs = 0;
    const _tbAxis = new Vector3();
    const _tbQ = new Quaternion();
    const _tbVel = new Vector3(); // angular velocity in world (rad/sec)
    const _tbInstVel = new Vector3(); // 瞬時角速度の作業用
    const _tbVelScratch = new Vector3();
    const TB_VEL_SMOOTH = 18; // 大きいほど velocity EMA が drag に俊敏に追従
    const TB_VEL_DECAY = 2.2; // 大きいほど早く慣性が止まる (halflife = ln2/dec)
    const TB_VEL_MIN = 0.05; // rad/sec、これを下回ったら停止扱い
    const onTrackballStart = (clientX: number, clientY: number): void => {
      _tbDragging = true;
      controlsActive = true;
      _tbLastX = clientX;
      _tbLastY = clientY;
      _tbLastMoveMs = performance.now();
      // 既存の慣性が残っていたら掴んだ時点で打ち切り (ハードキャッチ感)。
      _tbVel.set(0, 0, 0);
      scheduleNextAutoTrigger(nowSec() + 4);
    };
    const onTrackballMove = (clientX: number, clientY: number): void => {
      if (!_tbDragging) return;
      const dx = clientX - _tbLastX;
      const dy = clientY - _tbLastY;
      _tbLastX = clientX;
      _tbLastY = clientY;
      const dist = Math.hypot(dx, dy);
      if (dist < 1e-4) return;
      const nowMs = performance.now();
      const dt = Math.max((nowMs - _tbLastMoveMs) / 1000, 0.001);
      _tbLastMoveMs = nowMs;
      // 1px ≈ 0.005 rad (≈ 0.29°) を基準にする。
      const angle = dist * 0.005;
      _tbAxis.set(dy / dist, dx / dist, 0).applyQuaternion(camera.quaternion);
      _tbQ.setFromAxisAngle(_tbAxis, angle);
      root.quaternion.premultiply(_tbQ).normalize();
      // 慣性用 velocity を EMA で更新。
      _tbInstVel.copy(_tbAxis).multiplyScalar(angle / dt);
      const k = 1 - Math.exp(-TB_VEL_SMOOTH * dt);
      _tbVel.lerp(_tbInstVel, k);
    };
    const onTrackballEnd = (): void => {
      if (!_tbDragging) return;
      _tbDragging = false;
      // 慣性が残っているうちは controlsActive を維持して continuous motion 等が
      // 割り込まないようにする。updateFn 側の momentum tick で停止判定したら下げる。
      breathingBaseZoom = camera.zoom;
      breathingPhase = 0;
      scheduleNextAutoTrigger(nowSec() + 4);
    };
    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length === 0) return;
      e.preventDefault();
      onTrackballStart(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchMove = (e: TouchEvent): void => {
      if (e.touches.length === 0) return;
      e.preventDefault();
      onTrackballMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = (): void => onTrackballEnd();
    if (isMobile()) {
      const c = renderer.domElement;
      c.addEventListener("touchstart", onTouchStart, { passive: false });
      c.addEventListener("touchmove", onTouchMove, { passive: false });
      c.addEventListener("touchend", onTouchEnd);
      c.addEventListener("touchcancel", onTouchEnd);
    }

    const initialCameraZoom = camera.zoom;
    const initialCameraPos = camera.position.clone();
    const initialTarget = controls.target.clone();

    const MODES: DisplayMode[] = ["lines", "both", "dots", "grid"];
    let modeIdx = 1;

    let gridMode = false;
    // dots+ ペインの色を起動時にランダムで決定。HSL でやや暗めの中彩度。
    const GRID_ALT_COLOR = `hsl(${Math.floor(Math.random() * 360)} 60% 45%)`;

    // 表示モード切替のフェード。dots / labels は per-element opacity に
    // dotsModeOpacity を掛け合わせ、lines は LineMaterial.opacity に直接適用。
    // 2 / sec = 0.5 秒で完全遷移。
    const MODE_FADE_RATE = 2;
    let dotsModeOpacity = 1;
    let dotsModeTarget = 1;
    let linesModeOpacity = 1;
    let linesModeTarget = 1;
    // intro 終了後のラインのフェードイン: 0..1 で、1 が通常表示。
    const LINE_FADE_DURATION = 0.6;
    let linesIntroFadeT = 1;

    function applyMode(idx: number): void {
      modeIdx = ((idx % MODES.length) + MODES.length) % MODES.length;
      const mode = MODES[modeIdx];
      gridMode = mode === "grid";

      // grid モードでは CSS2D ドット / ラベルを瞬時に隠す (フェードは grid 出入りで
      // 違和感が出るのでスキップ)。lines は grid 内で per-pane に制御するので、
      // mode-fade target はここでは触らない。
      if (gridMode) {
        dotsModeTarget = 0;
        dotsModeOpacity = 0;
        linesModeTarget = 1;
        linesModeOpacity = 1;
      } else {
        const showPoints = mode !== "lines";
        const showLines = mode !== "dots";
        dotsModeTarget = showPoints ? 1 : 0;
        linesModeTarget = showLines ? 1 : 0;
        params.showPoints = showPoints;
        params.showLines = showLines;
        pointsCtrl.updateDisplay();
        linesCtrl.updateDisplay();
      }

      // grid 外では GridDots を全て非表示にして DOM 残留を防ぐ。
      if (!gridMode) gridDots.hideAll();

      if (modeEl) modeEl.textContent = mode;
    }

    let labelsVisible = false;

    function applyLabels(visible: boolean): void {
      labelsVisible = visible;
      // labels.group.visible は updateVisibility が labelsVisible を見て毎フレーム
      // 反映するので、ここでは状態フラグの更新のみ。
      if (labelToggleBtn) {
        labelToggleBtn.textContent = `labels: ${visible ? "on" : "off"}`;
        labelToggleBtn.classList.toggle("on", visible);
        labelToggleBtn.setAttribute("aria-pressed", String(visible));
      }
    }

    let occlusionEnabled = true;

    function applyOcclusion(enabled: boolean): void {
      occlusionEnabled = enabled;
      figure.setOcclusion(enabled);
      kaleidoscope.setOcclusion(enabled);
      // opacity / visible は updateVisibility が毎フレーム再計算するので、ここで
      // 即時に書き換える必要はない (occlusion OFF への切替は dotOpacities が 1 に
      // 向けて lerp されるため fade で自然に表れる)。
      if (occlusionToggleBtn) {
        occlusionToggleBtn.textContent = `occlusion: ${enabled ? "on" : "off"}`;
        occlusionToggleBtn.classList.toggle("on", enabled);
        occlusionToggleBtn.setAttribute("aria-pressed", String(enabled));
      }
    }

    // 立方体の 8 体対角線方向。各々が「頂点を画面手前に向ける視点」に対応し、
    // その視点では切頂立方体の頂点が常に 18 個視認可能になる。
    const DIAGONAL_TARGETS: Vector3[] = [];
    for (const sx of [-1, 1])
      for (const sy of [-1, 1])
        for (const sz of [-1, 1])
          DIAGONAL_TARGETS.push(new Vector3(sx, sy, sz).normalize());

    const _zAxis = new Vector3(0, 0, 1);
    const _slerpQuat = new Quaternion();
    const _twistQuat = new Quaternion();

    interface ViewAnim {
      startTime: number;
      duration: number;
      fromQuat: Quaternion;
      toQuat: Quaternion;
      fromCameraPos: Vector3;
      toCameraPos: Vector3;
      fromZoom: number;
      toZoom: number;
      animateCamera: boolean;
    }

    let viewAnim: ViewAnim | null = null;

    function easeInOutCubic(t: number): number {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function buildDiagonalQuat(): Quaternion {
      const target =
        DIAGONAL_TARGETS[Math.floor(Math.random() * DIAGONAL_TARGETS.length)];
      const quat = new Quaternion().setFromUnitVectors(target, _zAxis);
      // Z 軸まわりのランダムな twist で同じ対角線でも見え方を変える
      _twistQuat.setFromAxisAngle(_zAxis, Math.random() * Math.PI * 2);
      quat.premultiply(_twistQuat);
      return quat;
    }

    function startResetAnim(): void {
      // モバイル trackball 慣性が残っていると、viewAnim 終了後 (viewAnim=null) の
      // 次フレームから _tbVel が再び root.quaternion を回し始めてしまう。
      // reset 時は明示的に慣性をクリアし、controlsActive も下げて自動アニメへ
      // 復帰できるようにする。
      _tbVel.set(0, 0, 0);
      controlsActive = false;
      viewAnim = {
        startTime: performance.now() / 1000,
        duration: 0.9,
        fromQuat: root.quaternion.clone(),
        toQuat: INITIAL_QUAT.clone(),
        fromCameraPos: camera.position.clone(),
        toCameraPos: initialCameraPos.clone(),
        fromZoom: camera.zoom,
        toZoom: initialCameraZoom,
        animateCamera: true,
      };
    }

    const ZOOM_RANDOM_MIN = 0.5;
    const ZOOM_RANDOM_MAX = 0.75;

    function randomZoom(): number {
      return (
        ZOOM_RANDOM_MIN + Math.random() * (ZOOM_RANDOM_MAX - ZOOM_RANDOM_MIN)
      );
    }

    function startRandomAnim(animateZoom: boolean = false): void {
      // 慣性が残っていると viewAnim 終了後にスピンが復活するのでクリア。
      _tbVel.set(0, 0, 0);
      controlsActive = false;
      viewAnim = {
        startTime: performance.now() / 1000,
        duration: 1.2,
        fromQuat: root.quaternion.clone(),
        toQuat: buildDiagonalQuat(),
        fromCameraPos: camera.position.clone(),
        toCameraPos: camera.position.clone(),
        fromZoom: camera.zoom,
        toZoom: animateZoom ? randomZoom() : camera.zoom,
        animateCamera: animateZoom,
      };
    }

    function updateViewAnim(now: number): void {
      if (!viewAnim) return;
      const raw = (now - viewAnim.startTime) / viewAnim.duration;
      const done = raw >= 1;
      const t = done ? 1 : raw;
      const e = easeInOutCubic(t);

      _slerpQuat.copy(viewAnim.fromQuat).slerp(viewAnim.toQuat, e);
      root.quaternion.copy(_slerpQuat);

      if (viewAnim.animateCamera) {
        camera.position.lerpVectors(
          viewAnim.fromCameraPos,
          viewAnim.toCameraPos,
          e,
        );
        camera.zoom =
          viewAnim.fromZoom + (viewAnim.toZoom - viewAnim.fromZoom) * e;
        camera.updateProjectionMatrix();
      }

      syncRotateGUIFromRotation();

      if (done) {
        if (viewAnim.animateCamera) {
          controls.target.copy(initialTarget);
          controls.update();
        }
        viewAnim = null;
        // slerp 終了直後の zoom を呼吸の基準に取り直すことで、ランダム視点で得た
        // 新ズームが次の breathing サイクルの中心になる。
        breathingBaseZoom = camera.zoom;
        breathingPhase = 0;
      }
    }

    function resetView(): void {
      startResetAnim();
      scheduleNextAutoTrigger(nowSec() + 4);
    }

    function randomView(): void {
      startRandomAnim(true);
      scheduleNextAutoTrigger();
    }

    const AUTO_INTERVAL_MIN = 5;
    const AUTO_INTERVAL_MAX = 10;
    const COLOR_INTERVAL_MIN = 3;
    const COLOR_INTERVAL_MAX = 8;

    let autoAnimateEnabled = false;
    let autoNextTrigger = 0;
    let autoColorEnabled = false;
    let autoColorNextTrigger = 0;

    // 連続モーション: view 軸まわりの自転 + 数秒おきの体対角線切替 + sin の呼吸ズーム。
    // - 自転はカメラ方向 (view 軸) まわりの回転なのでシルエットの 12 角形を保つ
    // - 体対角線切替で「裏側に回り込む」遷移が入り、cube が立体的に動いて見える
    // - slerp 中や OrbitControls 操作中は休止し、終了時に breathingBaseZoom を
    //   取り直すことで wheel ズーム結果が次の呼吸サイクルの基準になる。
    const BREATHING_FREQ_HZ = 0.12;
    const BREATHING_AMP = 0.07;

    let continuousMotionEnabled = false;
    let breathingBaseZoom = 1 / 1.5;
    let breathingPhase = 0;
    let controlsActive = false;

    // 数値スライダーの「ねっとり追従」イージング。
    // 各スライダーは target だけ即座に更新、rendered current が指数減衰で 0.1s
    // 程度の lag をもって追従する。EASING_TAU は時定数 (e^-1 で約 63% 到達)、
    // 0.04s に設定すると 0.1s で約 92% 到達でほぼ完了。
    const EASING_TAU = 0.04;
    // ポイントサイズの自動計算。viewport の高さと target zoom から「cube の
    // 投影半径 (px)」を求め、その 30% を点サイズとする。
    // - 原理: 正射影で 1 world unit ≒ viewport_height/2 × zoom px。cube 頂点は
    //   world 半径 ≒ 0.82 なので、cube_radius_px ≒ 0.41 × h × zoom。
    // - 30% は body-diagonal 投影で最も近い頂点ペアが重ならない経験値。
    // - 端末ごとに viewport_height / MOBILE_SCALE が変わるので、PC でも
    //   小さいスマホでも比率を保ったまま見た目が揃う。
    // PC は viewport に依らず固定 100 px (ブランド仕様)。モバイルだけ画面サイズに
    // 応じて自動計算する。
    const PC_POINT_SIZE = 100;
    // モバイルは cube 自体を MOBILE_SCALE で大きく縮めているので、点も同じ比率に
    // すると視認性が下がる。専用比率でブランドモックに近づける。0.28 を基準に、
    // cube 全体 ×1.10 / 点 ×1.05 の最終見た目を満たすよう逆算 (0.28 × 1.05 / 1.10) ≒ 0.267。
    const POINT_RATIO_MOBILE = 0.267;
    const CUBE_WORLD_RADIUS = 0.82;
    function computeAutoPointSize(): number {
      if (!isMobile()) return PC_POINT_SIZE;
      const h = window.innerHeight;
      const targetZoom = baseZoom * MOBILE_SCALE;
      const cubeRadiusPx = 0.5 * CUBE_WORLD_RADIUS * h * targetZoom;
      return Math.max(8, Math.min(120, Math.round(cubeRadiusPx * POINT_RATIO_MOBILE)));
    }
    let pointSizeTarget = computeAutoPointSize();
    let pointSizeCurrent = pointSizeTarget;
    let lineWidthTarget = 1.5;
    let lineWidthCurrent = 1.5;
    let motionSpeedTarget = 6;
    let motionSpeedCurrent = 6;

    // === 「常時遷移」スタイルの連続モーション ===
    // 体対角線 A → B → C → ... を切れ目なく繋いだ滑らかな旅を作る。
    //   - currentDiagIdx → nextDiagIdx の間を transitionT (0→1) で補間
    //   - 到達したら次の体対角線へ即遷移開始 (待機ゼロ = 常に動いてる)
    //   - 反対側 (180°、antipodal) はスキップして補間の縮退を回避
    //   - 補間ベクトル (cube 局所空間) を view 軸へ強く tracking → cube が
    //     立体的に裏側へ回り込む動きになる
    //   - 加えて view 軸まわりの spin (motionSpeed) も乗せて自転感をキープ
    // tracking は強め (3〜5/s) にしないと target に追従できないが、強くしても
    // 突発的に見えないのは「target 自体が連続して動いている」から。
    const TARGET_TRACK_RATE = 4.0; // per second
    const TRANSITION_DURATION = 5.0; // 1 体対角線ぶんの遷移にかける秒数
    const _attrViewAxis = new Vector3();
    const _attrDiagWorld = new Vector3();
    const _attrCorrectQuat = new Quaternion();
    const _attrSmallQuat = new Quaternion();
    const _attrIdentityQuat = new Quaternion(); // 恒等回転 (0,0,0,1)
    const _spinQuat = new Quaternion();
    const _targetDirLocal = new Vector3();

    // 遷移の現在/次の体対角線 + 進行 t (0..1)。常時動く。
    let currentDiagIdx = 0;
    let nextDiagIdx = 1;
    let transitionT = 0;

    /** view 軸との内積が最大の体対角線インデックスを返す。 */
    function findNearestDiagonalIndex(): number {
      _attrViewAxis.copy(camera.position).normalize();
      let bestIdx = 0;
      let bestDot = -Infinity;
      for (let i = 0; i < DIAGONAL_TARGETS.length; i++) {
        _attrDiagWorld
          .copy(DIAGONAL_TARGETS[i])
          .applyQuaternion(root.quaternion);
        const d = _attrDiagWorld.dot(_attrViewAxis);
        if (d > bestDot) {
          bestDot = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    }

    /**
     * current と反対側 (= 180° antipodal、index = 7 - current) の両方を除いた
     * 体対角線インデックスからランダムに 1 つ返す。
     * antipodal を除く理由: lerp 補間が原点へ縮退してしまう (Vector3.lerp で
     * t=0.5 がゼロベクトルになり normalize 不能になる) ため。
     */
    function pickNonOppositeDiagonalIndex(current: number): number {
      const n = DIAGONAL_TARGETS.length;
      const opposite = n - 1 - current;
      // 候補は n - 2 個 (= 6)。current/opposite を skip しながら index を選ぶ。
      let pick = Math.floor(Math.random() * (n - 2));
      for (let i = 0; i < n; i++) {
        if (i === current || i === opposite) continue;
        if (pick === 0) return i;
        pick--;
      }
      return (current + 1) % n; // フォールバック
    }

    /**
     * targetLocal (cube 局所空間の単位ベクトル) を world で view 軸と
     * 一致するように root.quaternion を少しずつ補正する。
     * rate (per second) が大きいほど早く target に追従する。
     */
    function attractToDirection(targetLocal: Vector3, dt: number): void {
      _attrViewAxis.copy(camera.position).normalize();
      _attrDiagWorld.copy(targetLocal).applyQuaternion(root.quaternion);
      const dot = _attrDiagWorld.dot(_attrViewAxis);
      if (dot > 0.9995) return; // ほぼ揃っている
      _attrCorrectQuat.setFromUnitVectors(_attrDiagWorld, _attrViewAxis);
      const frac = Math.min(1, dt * TARGET_TRACK_RATE);
      _attrSmallQuat.copy(_attrIdentityQuat).slerp(_attrCorrectQuat, frac);
      root.quaternion.premultiply(_attrSmallQuat);
    }

    function nowSec(): number {
      return performance.now() / 1000;
    }

    function scheduleNextAutoTrigger(base: number = nowSec()): void {
      const span = AUTO_INTERVAL_MAX - AUTO_INTERVAL_MIN;
      autoNextTrigger = base + AUTO_INTERVAL_MIN + Math.random() * span;
    }

    function setAutoAnimate(enabled: boolean): void {
      autoAnimateEnabled = enabled;
      if (enabled) scheduleNextAutoTrigger();
    }

    function scheduleNextColorTrigger(base: number = nowSec()): void {
      const span = COLOR_INTERVAL_MAX - COLOR_INTERVAL_MIN;
      autoColorNextTrigger = base + COLOR_INTERVAL_MIN + Math.random() * span;
    }

    function setAutoColor(enabled: boolean): void {
      autoColorEnabled = enabled;
      if (enabled) scheduleNextColorTrigger();
    }

    function setContinuousMotion(enabled: boolean): void {
      continuousMotionEnabled = enabled;
      if (enabled) {
        breathingBaseZoom = camera.zoom;
        breathingPhase = 0;
        // 開始時の cube 向きから最も近い体対角線を「current」として、その隣の
        // 体対角線 (非反対側) を「next」として設定。途切れなく動き出す。
        currentDiagIdx = findNearestDiagonalIndex();
        nextDiagIdx = pickNonOppositeDiagonalIndex(currentDiagIdx);
        transitionT = 0;
      }
    }

    function updateContinuousMotion(dt: number): void {
      if (!continuousMotionEnabled) return;
      if (viewAnim) return;
      if (controlsActive) return;

      // 1) 遷移 t を進める。1 到達で current → next、新しい next を選び続行。
      //    待機なしで次の遷移に直結するので cube は常に動いている。
      //    kaleidoscope の有無で回転挙動を変えない (コピーも mainQuat 同期で
      //    同じ動きになる)。
      transitionT += dt / TRANSITION_DURATION;
      while (transitionT >= 1) {
        transitionT -= 1;
        currentDiagIdx = nextDiagIdx;
        nextDiagIdx = pickNonOppositeDiagonalIndex(currentDiagIdx);
      }

      // 2) ease-in-out で補間した「目標方向」を local 空間で構築。
      const easedT = easeInOutCubic(transitionT);
      _targetDirLocal
        .copy(DIAGONAL_TARGETS[currentDiagIdx])
        .lerp(DIAGONAL_TARGETS[nextDiagIdx], easedT)
        .normalize();

      // 3) view 軸まわりの spin: カメラ方向を回転軸にして cube を自転させる。
      //    view 軸まわりの回転は体対角線方向を動かさないので、シルエットの
      //    12 角形が保たれたまま内部の線だけが回って見える「自転」になる。
      _attrViewAxis.copy(camera.position).normalize();
      const rateRad = (motionSpeedCurrent * Math.PI) / 180;
      _spinQuat.setFromAxisAngle(_attrViewAxis, rateRad * dt);
      root.quaternion.premultiply(_spinQuat);

      // 4) target tracking: 補間した目標方向を view 軸へ合わせる。target
      //    自身が常時動いているので cube も常に追従して回転し続ける。
      attractToDirection(_targetDirLocal, dt);

      breathingPhase += dt * BREATHING_FREQ_HZ * Math.PI * 2;
      const breathed =
        breathingBaseZoom * (1 + Math.sin(breathingPhase) * BREATHING_AMP);
      // OrbitControls 側でも次フレーム clamp が走るが、上限を超えると一時的に
      // 「貼り付く」見え方が出るので、ここで先に [minZoom, maxZoom] に丸める。
      camera.zoom = Math.min(
        controls.maxZoom,
        Math.max(controls.minZoom, breathed),
      );
      camera.updateProjectionMatrix();
    }

    // === heartbeat: 点を心拍リズムでサイズパルスさせる ===
    // BPM で周期を決め、lub-dub (主拍 + 副拍 → 安静) のエンベロープでサイズを
    // 脈動させる。randomize=true で各点に別位相を与え、ばらばらに鼓動させる。
    // metaball ON 時は blob の重みに、kaleidoscope ON 時はコピーにも反映。
    const heartbeatParams = {
      enabled: false,
      bpm: 72,
      randomize: false, // false=全点同時 / true=各点ランダム位相
      amplitude: 0.6, // パルスの強さ (ピークで size × (1+amplitude))
    };
    let heartbeatElapsed = 0;
    const heartbeatPhases = new Float32Array(figureData.vertices.length);
    // 各点の現在のサイズ倍率 (1.0 = 通常)。disabled 時は全て 1.0。
    const heartbeatFactors = new Float32Array(figureData.vertices.length).fill(
      1,
    );
    // heartbeat 適用後の per-vertex 実サイズ (kaleidoscope へ渡す用)。
    const heartbeatSizes = new Float32Array(figureData.vertices.length);

    function regenHeartbeatPhases(): void {
      for (let i = 0; i < heartbeatPhases.length; i++) {
        heartbeatPhases[i] = heartbeatParams.randomize ? Math.random() : 0;
      }
    }
    regenHeartbeatPhases();

    // 心拍エンベロープ。位相 p∈[0,1) で主拍 (p≈0.12) + 副拍 (p≈0.30) → 安静。
    // ピーク ≈ 1.0。
    function heartbeatEnvelope(p: number): number {
      const a = Math.exp(-Math.pow((p - 0.12) / 0.06, 2));
      const b = 0.55 * Math.exp(-Math.pow((p - 0.3) / 0.06, 2));
      return a + b;
    }

    function updateHeartbeat(dt: number): void {
      if (!heartbeatParams.enabled) {
        heartbeatFactors.fill(1);
        return;
      }
      heartbeatElapsed += dt;
      const cyclesPerSec = heartbeatParams.bpm / 60;
      for (let i = 0; i < heartbeatFactors.length; i++) {
        let ph = (heartbeatElapsed * cyclesPerSec + heartbeatPhases[i]) % 1;
        if (ph < 0) ph += 1;
        heartbeatFactors[i] =
          1 + heartbeatParams.amplitude * heartbeatEnvelope(ph);
      }
    }

    const DEFAULT_POINT_COLOR = "#bc906b";
    const DEFAULT_LINE_COLOR = "#1a1a1a";

    const params = {
      pointSize: pointSizeTarget,
      pointColor: DEFAULT_POINT_COLOR,
      lineWidth: 1.5,
      lineColor: DEFAULT_LINE_COLOR,
      showPoints: true,
      showLines: true,
      rotateX: INITIAL_ROT_X_DEG,
      rotateY: INITIAL_ROT_Y_DEG,
      rotateZ: INITIAL_ROT_Z_DEG,
      showAxes: false,
      autoAnimate: false,
      continuousMotion: false,
      motionSpeed: 6,
      autoColor: false,
      randomizeColors: () => {
        dots.setRandomColors();
      },
      introTime: 0,
      intro3Time: 0,
      intro4Time: 0,
    };
    axesGuide.setVisible(false);
    dots.setColor(DEFAULT_POINT_COLOR);

    const gui = new GUI({ title: "params", width: 240 });
    // モバイル端末では GUI を起動時に折りたたみ。HUD の ⚙ ボタンでトグル可能。
    const mobileEnv = isMobile();
    if (mobileEnv) gui.close();
    guiToggleBtn?.addEventListener("click", () => {
      // lil-gui の内部状態を見て開閉トグル
      if ((gui as unknown as { _closed: boolean })._closed) gui.open();
      else gui.close();
    });

    // GUI パネル先頭にロゴ切替セレクタ ( / キーと同じ操作を GUI からも)。
    const logoSel = { logo: shell.activeName };
    gui
      .add(logoSel, "logo", shell.logoNames)
      .name("logo")
      .onChange((v: string) => shell.switchTo(v));

    // 八角形比率の切替: 元 (正八角形) / Asset1 (四角寄り) / Asset2 (菱形寄り)。
    // 頂点数 24・辺 36 のトポロジは同一で位置 (XI) だけが変わるので、
    // 頂点を in-place 差し替え + ジオメトリ再構築で切り替える。
    const octagonSel = { octagon: "yaesu" as OctagonPreset };
    function applyOctagonPreset(preset: OctagonPreset): void {
      const newData = buildOctagonOnCube(OCTAGON_XI[preset]);
      // figureData.vertices を in-place 更新 (GridDots / metaball が同じ参照を
      // 毎フレーム読むため)。edges / vertexFaceNormals はトポロジ共通で不変。
      for (let i = 0; i < figureData.vertices.length; i++) {
        figureData.vertices[i] = newData.vertices[i];
      }
      figure.setGeometry(figureData);
      dots.setPositions(figureData.vertices);
      labels.setPositions(figureData.vertices);
      kaleidoscope.setData(figureData);
    }
    gui
      .add(octagonSel, "octagon", {
        "正八角形 (元)": "regular",
        "Asset 1 (四角寄り)": "square",
        "Asset 2 (菱形寄り)": "diamond",
        "Asset 3 (鋭角寄り)": "sharp",
      })
      .name("octagon")
      .onChange((v: OctagonPreset) => applyOctagonPreset(v));

    // 投影方式の切替: ortho (正射影) / perspective (透視)。カメラを差し替え、
    // 現在の位置・向き・zoom を引き継いで OrbitControls を作り直す。
    const projectionSel = { projection: "ortho" as "ortho" | "perspective" };
    function switchProjection(type: "ortho" | "perspective"): void {
      if (type === projectionType) return;
      const next: OrthographicCamera | PerspectiveCamera =
        type === "perspective" ? sceneCtx.perspectiveCamera : sceneCtx.camera;
      // 現在のカメラの位置・向き・zoom を引き継ぐ (見え方を保つ)。
      next.position.copy(camera.position);
      next.quaternion.copy(camera.quaternion);
      next.zoom = camera.zoom;
      next.updateProjectionMatrix();
      // controls を作り直す (古いものは破棄)。
      controls.dispose();
      camera = next;
      projectionType = type;
      controls = new OrbitControls(camera, renderer.domElement);
      configureControls(controls);
      // breathing の基準 zoom を取り直す。
      breathingBaseZoom = camera.zoom;
      breathingPhase = 0;
    }
    gui
      .add(projectionSel, "projection", ["ortho", "perspective"])
      .name("projection")
      .onChange((v: "ortho" | "perspective") => switchProjection(v));

    // GUI パネルの最後にバージョン表記を追加。子要素にしておけば lil-gui の
    // 折りたたみ・リサイズに自動追従する。
    const versionEl = document.createElement("div");
    versionEl.id = "version-tag";
    versionEl.textContent = `v${pkg.version}`;
    gui.domElement.appendChild(versionEl);
    function syncLabelOffset(pointSize: number): void {
      // ラベルを点の右端から 4px 離した位置に配置することで、大きな点でも
      // 中に埋もれず視認できる。
      document.documentElement.style.setProperty(
        "--label-offset",
        `${pointSize / 2 + 4}px`,
      );
    }

    const pointSizeCtrl = gui
      .add(params, "pointSize", 2, 150, 1)
      .name("point size")
      .onChange((v: number) => {
        pointSizeTarget = v;
      });
    gui
      .addColor(params, "pointColor")
      .name("point color")
      .onChange((v: string) => {
        dots.setColor(v);
        kaleidoscope.setDotColor(v);
        metaball.setColor(v);
      });
    gui.add(params, "randomizeColors").name("randomize colors");
    gui
      .add(params, "autoColor")
      .name("auto randomize colors")
      .onChange((v: boolean) => setAutoColor(v));
    gui
      .add(params, "lineWidth", 0.5, 8, 0.1)
      .name("line width")
      .onChange((v: number) => {
        lineWidthTarget = v;
      });
    gui
      .addColor(params, "lineColor")
      .name("line color")
      .onChange((v: string) => {
        figure.setLineColor(v);
        kaleidoscope.setLineColor(v);
      });
    const pointsCtrl = gui
      .add(params, "showPoints")
      .name("show points")
      .onChange((v: boolean) => {
        dotsModeTarget = v ? 1 : 0;
        syncModeFromVisibility();
      });
    const linesCtrl = gui
      .add(params, "showLines")
      .name("show lines")
      .onChange((v: boolean) => {
        linesModeTarget = v ? 1 : 0;
        syncModeFromVisibility();
      });

    // 6 枚の正八角形面の中心が交わる点 (= 立方体中心 = world 原点) を回転中心
    // として、root の Euler rotation を直接度数で操作する。スライダー値は
    // root.rotation.x/y/z (ラジアン) と 1 対 1 対応。
    const rotFolder = gui.addFolder("rotate (deg)");
    const rotXCtrl = rotFolder
      .add(params, "rotateX", -180, 180, 1)
      .name("X axis")
      .onChange((v: number) => {
        rotateXTarget = v;
      });
    const rotYCtrl = rotFolder
      .add(params, "rotateY", -180, 180, 1)
      .name("Y axis")
      .onChange((v: number) => {
        rotateYTarget = v;
      });
    const rotZCtrl = rotFolder
      .add(params, "rotateZ", -180, 180, 1)
      .name("Z axis")
      .onChange((v: number) => {
        rotateZTarget = v;
      });

    gui
      .add(params, "showAxes")
      .name("show axes")
      .onChange((v: boolean) => axesGuide.setVisible(v));

    gui
      .add(params, "autoAnimate")
      .name("auto animate")
      .onChange((v: boolean) => setAutoAnimate(v));

    const continuousMotionCtrl = gui
      .add(params, "continuousMotion")
      .name("continuous motion")
      .onChange((v: boolean) => setContinuousMotion(v));
    gui
      .add(params, "motionSpeed", 0, 30, 0.5)
      .name("motion speed")
      .onChange((v: number) => {
        motionSpeedTarget = v;
      });

    // === 万華鏡リング: メイン cube の周囲を六角格子で埋める同期回転コピー群 ===
    // メインと同じ大きさ・同じ表示モード (lines/dots/both) で、空白をなるべく
    // 詰める。enabled OFF のあいだは update() を skip (visible=false でも update
    // は早期 return するので副作用なし)。
    const kaleidoParams = {
      enabled: false,
      rings: 1, // 六角格子の段数 (1→6 / 2→18 / 3→36 コピー)。重いので既定は 1。
      // spacing は「外周頂点がちょうど重なる距離」に対する倍率。実距離は
      // KaleidoscopeRing.update() が現在の投影外接半径から動的に算出するので、
      // 視点が変わっても倍率の意味 (1.0=ちょうど重なる) が保たれる。
      spacing: 1.0,
      scale: 1.0, // メインと同じ大きさ
      falloff: 0.45, // 距離ごとの opacity 減衰 (1.0=減衰なし, 小さいほど外周が薄い)
    };
    // 初期値を ring に反映 (コピー生成は enabled ON 時に setRings で行う)
    kaleidoscope.setSpacingFactor(kaleidoParams.spacing);
    kaleidoscope.setScale(kaleidoParams.scale);
    kaleidoscope.setCopyOpacity(kaleidoParams.falloff);
    const kaleFolder = gui.addFolder("kaleidoscope");
    kaleFolder
      .add(kaleidoParams, "enabled")
      .name("enabled")
      .onChange((v: boolean) => {
        kaleidoscope.setVisible(v);
        if (v) {
          // 有効化時にコピーを生成 + 現在のドット色/サイズを反映。
          kaleidoscope.setRings(kaleidoParams.rings);
          kaleidoscope.setDotColor(params.pointColor);
          kaleidoscope.setDotSize(pointSizeCurrent);
        }
      });
    kaleFolder
      .add(kaleidoParams, "rings", 1, 3, 1)
      .name("rings")
      .onChange((v: number) => {
        if (kaleidoParams.enabled) {
          kaleidoscope.setRings(v);
          kaleidoscope.setDotColor(params.pointColor);
          kaleidoscope.setDotSize(pointSizeCurrent);
        }
      });
    kaleFolder
      .add(kaleidoParams, "spacing", 0.5, 1.6, 0.01)
      .name("spacing (1=接)")
      .onChange((v: number) => kaleidoscope.setSpacingFactor(v));
    kaleFolder
      .add(kaleidoParams, "scale", 0.3, 1.5, 0.05)
      .name("scale")
      .onChange((v: number) => kaleidoscope.setScale(v));
    kaleFolder
      .add(kaleidoParams, "falloff", 0.1, 1.0, 0.05)
      .name("falloff")
      .onChange((v: number) => kaleidoscope.setCopyOpacity(v));

    // === メタボール: 24 頂点を画面空間メタボールで blob 化 ===
    // 逆距離場の和 → しきい値で「点が溶けて繋がる」表現。strength を上げる /
    // threshold を下げると blob が大きく繋がりやすくなる。enabled の間は
    // CSS2D ドットを隠して blob に置き換える。
    const metaballParams = {
      enabled: false,
      strength: 0.015, // 各点の場の強さ (大きいほど blob が広がる)
      threshold: 0.7, // しきい値 (小さいほど blob が太く繋がりやすい)
      // エッジの柔らかさ。0 = 記事と同じ step (完全な 0/1 のハードエッジ)、
      // 上げると smoothstep でフェード。
      smoothness: 0.0,
    };
    metaball.setStrength(metaballParams.strength);
    metaball.setThreshold(metaballParams.threshold);
    metaball.setSmoothness(metaballParams.smoothness);
    metaball.setColor(params.pointColor);
    const metaFolder = gui.addFolder("metaball");
    metaFolder.add(metaballParams, "enabled").name("enabled");
    metaFolder
      .add(metaballParams, "strength", 0.01, 0.6, 0.001)
      .name("strength")
      .onChange((v: number) => metaball.setStrength(v));
    metaFolder
      .add(metaballParams, "threshold", 0.1, 1.5, 0.001)
      .name("threshold")
      .onChange((v: number) => metaball.setThreshold(v));
    metaFolder
      .add(metaballParams, "smoothness", 0.0, 0.3, 0.005)
      .name("smoothness (0=step)")
      .onChange((v: number) => metaball.setSmoothness(v));

    // === heartbeat: 点を心拍リズムでサイズパルス ===
    const hbFolder = gui.addFolder("heartbeat");
    hbFolder
      .add(heartbeatParams, "enabled")
      .name("enabled")
      .onChange((v: boolean) => {
        if (!v) {
          // 解除時は一律サイズに戻す (per-dot サイズの残留を消す)。
          dots.setSize(pointSizeCurrent);
          kaleidoscope.setDotSize(pointSizeCurrent);
        }
      });
    hbFolder.add(heartbeatParams, "bpm", 20, 200, 1).name("bpm");
    hbFolder
      .add(heartbeatParams, "randomize")
      .name("randomize (位相)")
      .onChange(() => regenHeartbeatPhases());
    hbFolder.add(heartbeatParams, "amplitude", 0, 1.5, 0.05).name("amplitude");

    // === 動画書き出し (1920×1080 / 60fps / WebCodecs オフライン) ===
    // ループ概念がない cube ロゴでは、ユーザー指定の duration 秒間そのまま
    // 実時間でフレームを記録する (RAF 駆動の自然な動きが撮れる)。タイム
    // スタンプは performance.now() ベースなので RAF 落ちがあっても動画
    // 自体の再生速度は維持される。
    const exportStatusEl =
      cubeHud.querySelector<HTMLSpanElement>("#export-status");
    const setExportStatus = (text: string): void => {
      if (exportStatusEl) exportStatusEl.textContent = text;
    };

    interface RecordingState {
      encoder: OfflineVideoEncoder;
      compositor: CanvasFrameCompositor;
      ext: VideoExt;
      name: string;
      startTime: number; // performance.now() 値
      durationMs: number;
      frameCount: number;
      fps: number;
    }
    let recording: RecordingState | null = null;

    const startExport = async (ext: VideoExt): Promise<void> => {
      if (recording) return;
      const duration = exportParams.duration;
      const fps = 60;
      const width = 1920;
      const height = 1080;
      const bitrate = 12_000_000;
      if (!(await detectVideoFormat(ext, width, height, fps, bitrate))) {
        setExportStatus(`${ext} not supported`);
        return;
      }
      setExportStatus(`preparing ${ext}…`);
      try {
        const encoder = await OfflineVideoEncoder.create(
          ext,
          width,
          height,
          fps,
          bitrate,
        );
        const compositor = new CanvasFrameCompositor(width, height);
        recording = {
          encoder,
          compositor,
          ext,
          name: exportParams.filename || "cube",
          startTime: performance.now(),
          durationMs: duration * 1000,
          frameCount: 0,
          fps,
        };
        setExportStatus(`recording 0.0s / ${duration}s`);
      } catch (e) {
        setExportStatus(
          `export init failed: ${e instanceof Error ? e.message : String(e)}`,
        );
      }
    };

    const finishExport = async (): Promise<void> => {
      if (!recording) return;
      const rec = recording;
      recording = null;
      try {
        const blob = await rec.encoder.finalize();
        downloadBlob(blob, rec.name, rec.ext);
        setExportStatus(`saved: ${rec.name}.${rec.ext}`);
      } catch (e) {
        setExportStatus(
          `export failed: ${e instanceof Error ? e.message : String(e)}`,
        );
      }
    };

    // updateFn の最後に呼ぶフックでキャプチャ + エンコード。
    const captureFrameIfRecording = (): void => {
      if (!recording) return;
      const rec = recording;
      const elapsedMs = performance.now() - rec.startTime;
      if (elapsedMs >= rec.durationMs) {
        void finishExport();
        return;
      }
      // エンコーダのキューが詰まっていたらこのフレームはスキップ
      // (フレーム抜けは生じるが、エンコーダの内部エラーを防ぐ)。
      if (rec.encoder.queueSize > 10) {
        const secs = (elapsedMs / 1000).toFixed(1);
        const total = (rec.durationMs / 1000).toFixed(0);
        setExportStatus(`recording ${secs}s / ${total}s (encoder busy…)`);
        return;
      }
      try {
        rec.compositor.composite({
          src: canvas,
          srcWidth: window.innerWidth,
          srcHeight: window.innerHeight,
          overlayRoot: labelLayer,
          bgColor: "#ffffff",
        });
        // fire-and-forget だが Promise reject は明示的にハンドル
        // (これがないと「unhandled promise rejection」で赤いエラーになる)。
        rec.encoder
          .encodeFrame(
            rec.compositor.canvas,
            rec.frameCount % rec.fps === 0,
            Math.round(elapsedMs * 1000),
          )
          .catch((e: unknown) => {
            const msg = e instanceof Error ? e.message : String(e);
            console.error("[cube export] encodeFrame failed:", e);
            recording = null;
            setExportStatus(`export failed: ${msg}`);
          });
        rec.frameCount++;
        const secs = (elapsedMs / 1000).toFixed(1);
        const total = (rec.durationMs / 1000).toFixed(0);
        setExportStatus(`recording ${secs}s / ${total}s`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[cube export] composite failed:", e);
        recording = null;
        setExportStatus(`export failed: ${msg}`);
      }
    };

    const exportParams = {
      duration: 10,
      filename: "cube",
    };
    // intro アニメーションのタイムライン: time スライダーで任意の時刻に飛んで
    // 1 → 8 → 24 の遷移を確認できる。HUD の intro ボタンは「先頭から自動再生」、
    // ここの time は「スクラブ (任意の時点でホールド)」、exit で通常 cube へ復帰。
    const introFolder = gui.addFolder("intro animation");
    introFolder.add({ play: () => startIntro() }, "play").name("play");
    introTimeCtrl = introFolder
      .add(params, "introTime", 0, 1, 0.01)
      .name("time")
      .onChange((v: number) => scrubIntro(v));
    introFolder.add({ exit: () => exitIntro() }, "exit").name("exit intro");

    // intro3 用のタイムライン (cube 投影座標 + 鏡映パートナー設計)。
    const intro3Folder = gui.addFolder("intro3 animation");
    intro3Folder.add({ play: () => startIntro3() }, "play").name("play");
    intro3TimeCtrl = intro3Folder
      .add(params, "intro3Time", 0, 1, 0.01)
      .name("time")
      .onChange((v: number) => scrubIntro3(v));
    intro3Folder.add({ exit: () => exitIntro3() }, "exit").name("exit intro3");

    // intro4 用のタイムライン (1→8 中央寄せ ring までの新 intro)。
    const intro4Folder = gui.addFolder("intro4 animation");
    intro4Folder.add({ play: () => startIntro4() }, "play").name("play");
    intro4TimeCtrl = intro4Folder
      .add(params, "intro4Time", 0, 1, 0.01)
      .name("time")
      .onChange((v: number) => scrubIntro4(v));
    intro4Folder.add({ exit: () => exitIntro4() }, "exit").name("exit intro4");

    const expFolder = gui.addFolder("export (60fps, 1920×1080)");
    expFolder.add(exportParams, "duration", 1, 60, 1).name("duration (s)");
    expFolder.add(exportParams, "filename").name("filename");
    // exp webm / exp mp4 ボタンは HUD 側に統一 (他モードと位置を揃える)。
    expWebmBtn?.addEventListener("click", () => void startExport("webm"));
    expMp4Btn?.addEventListener("click", () => void startExport("mp4"));
    introBtn?.addEventListener("click", () => startIntro());
    intro3Btn?.addEventListener("click", () => startIntro3());
    intro4Btn?.addEventListener("click", () => startIntro4());

    // モバイル下部ボタン: スタート (= intro4 再生) / 元に戻す (= 初期視点)。
    mobileStartBtn?.addEventListener("click", () => startIntro4());
    mobileResetBtn?.addEventListener("click", () => resetView());

    function syncRotateGUIFromRotation(): void {
      params.rotateX = (root.rotation.x * 180) / Math.PI;
      params.rotateY = (root.rotation.y * 180) / Math.PI;
      params.rotateZ = (root.rotation.z * 180) / Math.PI;
      // slerp で root.rotation が動いたタイミングで eased state も実値に揃え、
      // 次に slider を触ったとき「前回値からの easing」でジャンプしないように。
      rotateXTarget = rotateXCurrent = params.rotateX;
      rotateYTarget = rotateYCurrent = params.rotateY;
      rotateZTarget = rotateZCurrent = params.rotateZ;
      rotXCtrl.updateDisplay();
      rotYCtrl.updateDisplay();
      rotZCtrl.updateDisplay();
    }

    function syncModeFromVisibility(): void {
      let next: DisplayMode | null = null;
      if (params.showPoints && params.showLines) next = "both";
      else if (params.showLines && !params.showPoints) next = "lines";
      else if (params.showPoints && !params.showLines) next = "dots";
      if (next === null) {
        if (modeEl) modeEl.textContent = "none";
        return;
      }
      modeIdx = MODES.indexOf(next);
      if (modeEl) modeEl.textContent = next;
    }

    dots.setSize(params.pointSize);
    syncLabelOffset(params.pointSize);
    applyMode(modeIdx);
    // モバイルは点のみのミニマル表示を固定。applyMode(2) の lerp を待たずに
    // 即座に lines opacity を 0 に落とし、フェード残像で「線あり」状態が
    // チラッと見えるのを防ぐ。
    if (isMobile()) {
      applyMode(2); // "dots" (= lines off, points on)
      linesModeOpacity = 0;
      linesModeTarget = 0;
      figure.setShowLines(false);
      figure.setLinesOpacity(0);
    }
    applyLabels(false);
    applyOcclusion(true);
    scheduleNextAutoTrigger();

    // モバイル ⇔ PC のレイアウト切替。DevTools のレスポンシブモードでもブレイク
    // ポイントを跨ぐと再適用される。マウント後にもう一度走らせて、マウント時の
    // 判定タイミング (Vite HMR 等) のばらつきを吸収する。
    const applyMobileLayout = (mobile: boolean): void => {
      const newZoom = mobile ? baseZoom * MOBILE_SCALE : baseZoom;
      camera.zoom = newZoom;
      camera.updateProjectionMatrix();
      sceneCtx.perspectiveCamera.zoom = newZoom;
      sceneCtx.perspectiveCamera.updateProjectionMatrix();
      // breathing は毎フレーム camera.zoom を breathingBaseZoom 基準に上書きするので、
      // ここで base も更新しないとモバイル zoom がすぐ PC 値に戻ってしまう。
      breathingBaseZoom = newZoom;
      // OrbitControls の minZoom クランプも合わせて下げないと、breathing loop で
      // 強制的に PC 値まで持ち上げられる。configureControls と同じ式を使う。
      if (projectionType === "ortho") {
        const cMin = baseZoom * 0.75;
        const cMax = baseZoom * 1.125;
        controls.minZoom = mobile ? cMin * MOBILE_SCALE : cMin;
        controls.maxZoom = mobile ? cMax * MOBILE_SCALE : cMax;
      }
      const newPointSize = computeAutoPointSize();
      pointSizeTarget = newPointSize;
      pointSizeCurrent = newPointSize;
      params.pointSize = newPointSize;
      pointSizeCtrl.updateDisplay();
      dots.setSize(newPointSize);
      syncLabelOffset(newPointSize);
    };
    // マウント直後にもう一度評価。HMR 残存値や初期化順の影響で
    // initialZoom がずれていた場合の補正。
    applyMobileLayout(isMobile());
    const mobileMql = window.matchMedia("(max-width: 768px)");
    const onMobileChange = (e: MediaQueryListEvent): void =>
      applyMobileLayout(e.matches);
    mobileMql.addEventListener("change", onMobileChange);

    // resize 時にも viewport から自動で点サイズを再計算する。breakpoint を
    // 跨がない範囲のサイズ変化 (横向き ⇔ 縦向き / ウィンドウサイズ調整) でも
    // 点のサイズが cube に対して同じ比率を保つように追従する。
    const applyAutoPointSize = (): void => {
      const sz = computeAutoPointSize();
      pointSizeTarget = sz;
      pointSizeCurrent = sz;
      params.pointSize = sz;
      pointSizeCtrl.updateDisplay();
      dots.setSize(sz);
      syncLabelOffset(sz);
    };
    window.addEventListener("resize", applyAutoPointSize);

    labelToggleBtn?.addEventListener("click", () =>
      applyLabels(!labelsVisible),
    );
    occlusionToggleBtn?.addEventListener("click", () =>
      applyOcclusion(!occlusionEnabled),
    );
    resetBtn?.addEventListener("click", () => resetView());
    randomBtn?.addEventListener("click", () => randomView());

    function toggleContinuousMotion(): void {
      const target = !continuousMotionEnabled;
      setContinuousMotion(target);
      params.continuousMotion = target;
      continuousMotionCtrl.updateDisplay();
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      // input / textarea / select に入力中はショートカットを無視
      // (lil-gui の数値入力欄などでの誤発火を防ぐ)。
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "1") applyMode(0);
      else if (e.key === "2") applyMode(1);
      else if (e.key === "3") applyMode(2);
      else if (e.key === "4") applyMode(3);
      else if (e.key === "m" || e.key === "M") applyMode(modeIdx + 1);
      else if (e.key === "l" || e.key === "L") applyLabels(!labelsVisible);
      else if (e.key === "o" || e.key === "O")
        applyOcclusion(!occlusionEnabled);
      else if (e.key === "n" || e.key === "N") randomView();
      else if (e.key === "r" || e.key === "R") resetView();
      else if (e.code === "Space") {
        e.preventDefault();
        toggleContinuousMotion();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    const _cameraWorld = new Vector3();
    const _vertWorld = new Vector3();
    const _normalWorld = new Vector3();
    const _toCamera = new Vector3();
    const _rotMat = new Matrix4();

    /**
     * 面がカメラ向きとみなす閾値。厳密に dot > 0 にすると、面がちょうど視線と
     * 直角な「縁の頂点」で浮動小数点の丸めで揺れて見えなくなるため、わずかな
     * 負側マージンを持たせて「ほぼ直角」も visible 側に倒す。
     */
    const FACE_VISIBLE_EPSILON = 0.05;

    function lerpTowards(
      current: number,
      target: number,
      step: number,
    ): number {
      const diff = target - current;
      return Math.abs(diff) <= step ? target : current + Math.sign(diff) * step;
    }

    /**
     * スライダー値の指数減衰追従。`target` を即時セットしたあと毎フレーム呼び、
     * `current` が EASING_TAU の時定数で `target` へ漸近。差分が小さくなったら
     * snap して計算をやめる (FP 浮動の永続走行を防ぐ)。
     */
    function updateEasedSliders(dt: number): void {
      const alpha = 1 - Math.exp(-dt / EASING_TAU);

      if (pointSizeCurrent !== pointSizeTarget) {
        const next =
          pointSizeCurrent + (pointSizeTarget - pointSizeCurrent) * alpha;
        pointSizeCurrent =
          Math.abs(next - pointSizeTarget) < 0.05 ? pointSizeTarget : next;
        dots.setSize(pointSizeCurrent);
        kaleidoscope.setDotSize(pointSizeCurrent);
        syncLabelOffset(pointSizeCurrent);
      }

      if (lineWidthCurrent !== lineWidthTarget) {
        const next =
          lineWidthCurrent + (lineWidthTarget - lineWidthCurrent) * alpha;
        lineWidthCurrent =
          Math.abs(next - lineWidthTarget) < 0.005 ? lineWidthTarget : next;
        figure.setLineWidth(lineWidthCurrent);
        kaleidoscope.setLineWidth(lineWidthCurrent);
      }

      if (motionSpeedCurrent !== motionSpeedTarget) {
        const next =
          motionSpeedCurrent + (motionSpeedTarget - motionSpeedCurrent) * alpha;
        motionSpeedCurrent =
          Math.abs(next - motionSpeedTarget) < 0.01 ? motionSpeedTarget : next;
      }

      // rotate は current !== target のときだけ root.rotation を上書きする。
      // 一致したあとは触れないので、continuous motion や slerp が自由に
      // root.quaternion を動かせる (常時競合を避ける)。
      if (rotateXCurrent !== rotateXTarget) {
        const next = rotateXCurrent + (rotateXTarget - rotateXCurrent) * alpha;
        rotateXCurrent =
          Math.abs(next - rotateXTarget) < 0.05 ? rotateXTarget : next;
        root.rotation.x = (rotateXCurrent * Math.PI) / 180;
      }
      if (rotateYCurrent !== rotateYTarget) {
        const next = rotateYCurrent + (rotateYTarget - rotateYCurrent) * alpha;
        rotateYCurrent =
          Math.abs(next - rotateYTarget) < 0.05 ? rotateYTarget : next;
        root.rotation.y = (rotateYCurrent * Math.PI) / 180;
      }
      if (rotateZCurrent !== rotateZTarget) {
        const next = rotateZCurrent + (rotateZTarget - rotateZCurrent) * alpha;
        rotateZCurrent =
          Math.abs(next - rotateZTarget) < 0.05 ? rotateZTarget : next;
        root.rotation.z = (rotateZCurrent * Math.PI) / 180;
      }
    }

    /**
     * モードフェード + オクルージョンを統合した毎フレームの visibility 更新。
     *
     * 1. dotsModeOpacity / linesModeOpacity を目標値へ MODE_FADE_RATE で lerp
     * 2. lines はマテリアル opacity に直接適用、ほぼ 0 で render スキップ
     * 3. 各頂点はオクルージョン opacity (per-vertex, FADE_RATE で lerp) と
     *    dotsModeOpacity の積を element に反映。occlusion OFF 時は全頂点 1 扱い
     */
    function updateVisibility(dt: number): void {
      const modeStep = MODE_FADE_RATE * dt;
      dotsModeOpacity = lerpTowards(dotsModeOpacity, dotsModeTarget, modeStep);
      linesModeOpacity = lerpTowards(
        linesModeOpacity,
        linesModeTarget,
        modeStep,
      );

      figure.setLinesOpacity(linesModeOpacity);
      figure.setShowLines(linesModeOpacity > 0.001);
      kaleidoscope.setLinesOpacity(linesModeOpacity);
      kaleidoscope.setShowLines(linesModeOpacity > 0.001);
      kaleidoscope.setDotsVisible(dotsModeOpacity > 0.001);

      dots.group.visible = dotsModeOpacity > 0.001;
      labels.group.visible = labelsVisible && dotsModeOpacity > 0.001;
      // 早期 return は使わない: dots.group.visible=false にしても、CSS2DObject の
      // element (DOM div) は最後の opacity / transform を保持し続けるため、loop で
      // 毎フレーム opacity を 0 に更新しないと、grid モードに切り替えたとき過去の
      // ノーマルモードのドット位置に居残って見える。
      const dotChildren = dots.group.children;
      const labelChildren = labels.group.children;
      const occStep = FADE_RATE * dt;

      if (introActive) {
        // intro 中: 通常のオクルージョン計算は走らせない。intro tick 側で
        // ドット位置・opacity を全点 1 に上書き済み。
      } else if (occlusionEnabled) {
        camera.getWorldPosition(_cameraWorld);
        _rotMat.extractRotation(root.matrixWorld);
        const faceNormals = figureData.vertexFaceNormals;
        for (let i = 0; i < dotChildren.length; i++) {
          const dot = dotChildren[i];
          dot.getWorldPosition(_vertWorld);
          _toCamera.subVectors(_cameraWorld, _vertWorld).normalize();
          const fs = faceNormals[i];
          let visible = false;
          for (let f = 0; f < 3; f++) {
            const n = fs[f];
            _normalWorld.set(n[0], n[1], n[2]).applyMatrix4(_rotMat);
            if (_toCamera.dot(_normalWorld) > -FACE_VISIBLE_EPSILON) {
              visible = true;
              break;
            }
          }
          dotOpacities[i] = lerpTowards(
            dotOpacities[i],
            visible ? 1 : 0,
            occStep,
          );
          const finalOp = dotOpacities[i] * dotsModeOpacity;
          dotFinalOpacities[i] = finalOp;
          const renderOn = finalOp > 0.001;
          dot.visible = renderOn;
          dots.setOpacity(i, finalOp);
          if (labelChildren[i]) {
            labelChildren[i].visible = renderOn;
            labels.setOpacity(i, finalOp);
          }
        }
      } else {
        for (let i = 0; i < dotChildren.length; i++) {
          dotOpacities[i] = lerpTowards(dotOpacities[i], 1, occStep);
          const finalOp = dotOpacities[i] * dotsModeOpacity;
          dotFinalOpacities[i] = finalOp;
          const renderOn = finalOp > 0.001;
          dotChildren[i].visible = renderOn;
          dots.setOpacity(i, finalOp);
          if (labelChildren[i]) {
            labelChildren[i].visible = renderOn;
            labels.setOpacity(i, finalOp);
          }
        }
      }

      // 万華鏡コピーのドットへ、メインで計算した per-vertex 最終 opacity を流用。
      // (全コピーはメインと同じ回転なので可視性は完全一致)
      kaleidoscope.applyVertexOpacities(dotFinalOpacities);
    }

    interface GridPane {
      cssX: number; // CSS top-left 基準
      cssY: number;
      w: number;
      h: number;
      showLines: boolean;
      showDots: boolean;
      dotColor: string;
    }

    const _gridVisibleIdx: number[] = [];
    // grid モードのペイン用 per-vertex opacity (24 頂点)。normal モードの
    // dotOpacities とは独立に管理。3 面 OR 判定の結果を target にして FADE_RATE
    // で lerp、視点回転で頂点が境界を通過するときの flicker を吸収する。
    const gridDotOpacities: number[] = new Array(
      figureData.vertices.length,
    ).fill(0);

    /**
     * 3 面 OR 法線判定で表面の頂点 index 配列を組み立てる (normal モードの
     * CSS2D ドットと同じロジック)。occlusion OFF のときは全 24 頂点を返す。
     */
    function computeGridVisibleIndices(): void {
      _gridVisibleIdx.length = 0;
      if (!occlusionEnabled) {
        for (let i = 0; i < figureData.vertices.length; i++)
          _gridVisibleIdx.push(i);
        return;
      }

      camera.getWorldPosition(_cameraWorld);
      _rotMat.extractRotation(root.matrixWorld);
      const faceNormals = figureData.vertexFaceNormals;

      for (let i = 0; i < figureData.vertices.length; i++) {
        const [x, y, z] = figureData.vertices[i];
        _vertWorld.set(x, y, z).applyMatrix4(root.matrixWorld);
        _toCamera.subVectors(_cameraWorld, _vertWorld).normalize();
        const fs = faceNormals[i];
        let visible = false;
        for (let f = 0; f < 3; f++) {
          const n = fs[f];
          _normalWorld.set(n[0], n[1], n[2]).applyMatrix4(_rotMat);
          if (_toCamera.dot(_normalWorld) > -FACE_VISIBLE_EPSILON) {
            visible = true;
            break;
          }
        }
        if (visible) _gridVisibleIdx.push(i);
      }
    }

    /**
     * grid モードの 4 ペイン分割描画。
     * - lines / hull は WebGL の viewport + scissor でペインごとに描画
     * - dots は DOM ベースの GridDots でペインごとに手動投影
     *
     * これにより 3D Points の billboard クリッピング / 角度依存の欠けを完全に
     * 回避し、normal モードの bottom-left (both) と同じ「常に完全な円」を保つ。
     */
    function renderGridPanes(dt: number): void {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const halfW = Math.floor(w / 2);
      const halfH = Math.floor(h / 2);
      // dots は CSS でペイン内に描くので、point size をペインスケール (0.5) に
      // 落として「全画面と同じ見え方」を保つ。
      const dotSize = Math.max(3, pointSizeCurrent * 0.5);

      // 4 ペインとも同じカメラ・回転なので、可視頂点判定は frame 内で 1 回。
      scene.updateMatrixWorld();
      computeGridVisibleIndices();

      // per-vertex opacity を lerp で更新。3 面 OR 判定の binary 結果を直接表示
      // 切替に使うと境界角で flicker するので、normal モードと同じ FADE_RATE で
      // softer transition させる。
      const occStep = FADE_RATE * dt;
      const visibleSet = new Set(_gridVisibleIdx);
      for (let i = 0; i < figureData.vertices.length; i++) {
        const target = visibleSet.has(i) ? 1 : 0;
        gridDotOpacities[i] = lerpTowards(
          gridDotOpacities[i],
          target,
          occStep,
        );
      }

      // CSS 座標系 (top-left 基準) のペイン定義
      const panes: GridPane[] = [
        {
          cssX: 0,
          cssY: 0,
          w: halfW,
          h: halfH,
          showLines: true,
          showDots: false,
          dotColor: DEFAULT_POINT_COLOR,
        }, // top-left: lines
        {
          cssX: halfW,
          cssY: 0,
          w: halfW,
          h: halfH,
          showLines: false,
          showDots: true,
          dotColor: DEFAULT_POINT_COLOR,
        }, // top-right: dots
        {
          cssX: 0,
          cssY: halfH,
          w: halfW,
          h: halfH,
          showLines: true,
          showDots: true,
          dotColor: DEFAULT_POINT_COLOR,
        }, // bottom-left: both
        {
          cssX: halfW,
          cssY: halfH,
          w: halfW,
          h: halfH,
          showLines: false,
          showDots: true,
          dotColor: GRID_ALT_COLOR,
        }, // bottom-right: dots+
      ];

      // DOM 側のドット更新
      const gridPaneCfgs: GridDotsPaneConfig[] = panes.map((p) => ({
        cssX: p.cssX,
        cssY: p.cssY,
        w: p.w,
        h: p.h,
        show: p.showDots,
        color: p.dotColor,
      }));
      gridDots.setSize(dotSize);
      gridDots.update(root, camera, gridDotOpacities, gridPaneCfgs);

      // WebGL 側 (lines + hull) はペインごとに viewport + scissor で描画。
      // WebGL viewport は左下原点なので CSS y を反転する。
      renderer.setScissorTest(true);
      for (const p of panes) {
        figure.setShowLines(p.showLines);
        const wglY = h - p.cssY - p.h;
        renderer.setViewport(p.cssX, wglY, p.w, p.h);
        renderer.setScissor(p.cssX, wglY, p.w, p.h);
        renderer.render(scene, camera);
      }
      renderer.setScissorTest(false);
    }

    // === フレーム本体 → updateFn (RAF と FPS はシェルが担う) ===
    this.updateFn = (dt: number): void => {
      const now = performance.now() / 1000;

      // モバイル trackball の慣性。指を離した直後も _tbVel に従って root を
      // 回し続け、TB_VEL_DECAY で指数減衰させる。intro / viewAnim 中は他のロジック
      // が root.quaternion を直接書くので干渉しないよう skip。
      if (
        !_tbDragging &&
        !introActive &&
        !intro3Active &&
        !intro4Active &&
        !viewAnim
      ) {
        const sq = _tbVel.lengthSq();
        if (sq > TB_VEL_MIN * TB_VEL_MIN) {
          const speed = Math.sqrt(sq);
          _tbVelScratch.copy(_tbVel).divideScalar(speed);
          _tbQ.setFromAxisAngle(_tbVelScratch, speed * dt);
          root.quaternion.premultiply(_tbQ).normalize();
          _tbVel.multiplyScalar(Math.exp(-TB_VEL_DECAY * dt));
        } else if (sq > 0) {
          // 閾値以下になったら停止 + controlsActive を下げて自動アニメ復帰。
          _tbVel.set(0, 0, 0);
          controlsActive = false;
        }
      }

      // intro アニメーション: 1 → 8 → 24 のシーケンス。通常の CSS2D ドットの位置
      // だけを intro 用に上書きし、ライン・ラベル・万華鏡は隠す。
      // introPlaying=true なら timer を自動進行 (auto-play)、false なら現在位置で
      // ホールド (スクラブ / 一時停止)。auto-play で末尾に達したら endIntro で
      // 通常 cube へハンドオフ (ライン fade-in)。
      if (introActive) {
        if (introPlaying) {
          introTimer += dt;
          if (introTimer >= INTRO_DURATION) {
            introTimer = INTRO_DURATION;
            introPlaying = false;
            endIntro();
          }
        }
        if (introActive) {
          const tNorm = introTimer / INTRO_DURATION;
          // intro 全期間で cube に追加スピンを連続的にかける (一方向)。t=0 で
          // INTRO_SPIN_ANGLE、t=1 で 0 へ単調 ease-in-out。phase 0/1/hold は
          // ring が画面 XY に張り付くので不可視、phase 2 で点が cube 位置へ
          // 動き始めた時点から左/右の回転として見える。
          const spinFade = introEaseInOut(tNorm);
          const angle = (1 - spinFade) * INTRO_SPIN_ANGLE;
          _introSpinQuat.setFromAxisAngle(_introZAxis, angle);
          root.quaternion.multiplyQuaternions(_introSpinQuat, INITIAL_QUAT);
          root.updateMatrixWorld();

          computeIntroVertices(tNorm);
          dots.setPositions(introVertices);
          dots.group.visible = true;
          figure.group.visible = false;
          labels.group.visible = false;
          kaleidoscope.group.visible = false;
          // GUI スライダーを現在位置に同期 (auto-play 中の進行も可視化)。
          params.introTime = tNorm;
          introTimeCtrl?.updateDisplay();
        }
      }

      // intro3: cube 投影座標 + 鏡映パートナー設計 + 6 点鏡開き (ghost 反射子)。
      if (intro3Active) {
        if (intro3Playing) {
          intro3Timer += dt;
          if (intro3Timer >= INTRO3_DURATION) {
            intro3Timer = INTRO3_DURATION;
            intro3Playing = false;
            endIntro3();
          }
        }
        if (intro3Active) {
          const tNorm = intro3Timer / INTRO3_DURATION;
          root.quaternion.copy(INITIAL_QUAT);
          root.updateMatrixWorld();
          computeIntro3Vertices(tNorm);
          dots.setPositions(introVertices);
          dots.group.visible = true;
          figure.group.visible = false;
          labels.group.visible = false;
          kaleidoscope.group.visible = false;
          params.intro3Time = tNorm;
          intro3TimeCtrl?.updateDisplay();
        }
      }

      // intro4: 1→8 ring → face-on cube → rotate → body-diagonal
      if (intro4Active) {
        if (intro4Playing) {
          intro4Timer += dt;
          if (intro4Timer >= INTRO4_DURATION) {
            intro4Timer = INTRO4_DURATION;
            intro4Playing = false;
            endIntro4();
          }
        }
        if (intro4Active) {
          const tNorm = intro4Timer / INTRO4_DURATION;
          // root.q をフェーズに応じて更新:
          //   0..HOLD_FACE_ON_END: identity (face-on view)
          //   HOLD_FACE_ON_END..ROTATE_END: slerp identity → INITIAL_QUAT (回転)
          //   ROTATE_END..1.0: INITIAL_QUAT (body-diagonal view)
          if (tNorm < INTRO4_HOLD_FACE_ON_END) {
            root.quaternion.identity();
          } else if (tNorm < INTRO4_ROTATE_END) {
            const u = introEaseInOut(
              (tNorm - INTRO4_HOLD_FACE_ON_END) /
                (INTRO4_ROTATE_END - INTRO4_HOLD_FACE_ON_END),
            );
            root.quaternion.identity().slerp(INITIAL_QUAT, u);
          } else {
            root.quaternion.copy(INITIAL_QUAT);
          }
          root.updateMatrixWorld();
          computeIntro4Vertices(tNorm);
          dots.setPositions(introVertices);
          dots.group.visible = true;
          figure.group.visible = false;
          labels.group.visible = false;
          kaleidoscope.group.visible = false;
          params.intro4Time = tNorm;
          intro4TimeCtrl?.updateDisplay();
        }
      }

      // intro / intro3 / intro4 中は通常の回転系処理 (view anim / continuous motion)
      // をスキップ。root.quaternion は各 intro tick で位相ごとに制御している。
      if (!introActive && !intro3Active && !intro4Active) {
        updateViewAnim(now);
      }
      updateEasedSliders(dt);
      if (!introActive && !intro3Active && !intro4Active) {
        updateContinuousMotion(dt);
      }

      // 自動アニメ: アニメ非実行 & 有効 & 次トリガー時刻を過ぎたらランダム視点へ。
      if (autoAnimateEnabled && !viewAnim && now >= autoNextTrigger) {
        startRandomAnim(true);
        scheduleNextAutoTrigger();
      }

      // 自動カラー: 視点アニメとは独立して、3〜8 秒ごとに全点のランダム色化を発火。
      // 色の遷移は CSS transition (background-color 0.5s) で自動的にイージング。
      if (autoColorEnabled && now >= autoColorNextTrigger) {
        dots.setRandomColors();
        scheduleNextColorTrigger();
      }

      controls.update();
      // 万華鏡: grid モードでは複数 viewport 描画と相性が悪いので非表示。
      // enabled かつ非 grid のときだけリングを表示し、各コピーの位置・回転を
      // 現在の view 軸 + メイン quaternion に同期する。
      if (kaleidoParams.enabled && !gridMode) {
        kaleidoscope.setVisible(true);
        kaleidoscope.update(camera.position, root.quaternion);
      } else {
        kaleidoscope.setVisible(false);
      }
      if (occlusionEnabled) scene.updateMatrixWorld();
      updateVisibility(dt);

      // heartbeat: 各点のサイズ倍率を更新。enabled 中は dots / kaleidoscope の
      // ドットサイズを per-vertex に脈動させる (metaball ON 時は重み側に反映)。
      updateHeartbeat(dt);
      if (heartbeatParams.enabled && !metaballParams.enabled) {
        for (let i = 0; i < heartbeatSizes.length; i++) {
          heartbeatSizes[i] = pointSizeCurrent * heartbeatFactors[i];
          dots.setSizeAt(i, heartbeatSizes[i]);
        }
        if (kaleidoParams.enabled && !gridMode) {
          kaleidoscope.applyVertexSizes(heartbeatSizes);
        }
      }

      // metaball ON 中は CSS2D ドットを隠して blob に置き換える (grid 以外)。
      // kaleidoscope コピーのドットも同様に隠す (blob に統合)。
      if (metaballParams.enabled && !gridMode) {
        dots.group.visible = false;
        kaleidoscope.setDotsVisible(false);
      }
      if (viewInfoEl) {
        const az = ((controls.getAzimuthalAngle() * 180) / Math.PI).toFixed(1);
        const pol = ((controls.getPolarAngle() * 180) / Math.PI).toFixed(1);
        const zm = camera.zoom.toFixed(2);
        viewInfoEl.textContent = `azimuth ${az}°  polar ${pol}°  zoom ${zm}`;
      }
      // intro 中は通常ロジックが visibility / opacity を上書きしてしまうので、
      // レンダリング直前に最終上書きを入れる。
      // cube の occlusion 状態 (各頂点の「最終 cube 位置」での visible/back) を
      // intro 全フェーズで適用する。back-facing の点は intro 最初から非表示 →
      // 分裂時にフェードせず、表面に出るべき点のみが 1 → 8 → 24 で動く。
      if (introActive || intro3Active || intro4Active) {
        dots.group.visible = true;
        figure.group.visible = false;
        labels.group.visible = false;
        kaleidoscope.group.visible = false;

        // 単一ドット時のみ通常より大きく、phase 1 で通常サイズへ smoothstep 縮小。
        // どの intro が active かに応じて該当 timer / 各定数を参照する。
        const activeDur = introActive
          ? INTRO_DURATION
          : intro3Active
            ? INTRO3_DURATION
            : INTRO4_DURATION;
        const activeTimer = introActive
          ? introTimer
          : intro3Active
            ? intro3Timer
            : intro4Timer;
        const activeP1Start = introActive
          ? INTRO_PHASE_1_START
          : intro3Active
            ? INTRO3_PHASE_1_START
            : INTRO4_PHASE_1_START;
        const activeP1End = introActive
          ? INTRO_PHASE_1_END
          : intro3Active
            ? INTRO3_PHASE_1_END
            : INTRO4_PHASE_1_END;
        const tIntro = activeTimer / activeDur;
        let sizeMult = 1;
        if (tIntro < activeP1Start) {
          sizeMult = INTRO_BIG_SIZE_MULT;
        } else if (tIntro < activeP1End) {
          const u =
            (tIntro - activeP1Start) / (activeP1End - activeP1Start);
          const e = u * u * (3 - 2 * u); // smoothstep
          sizeMult = INTRO_BIG_SIZE_MULT * (1 - e) + 1 * e;
        }
        dots.setSize(pointSizeCurrent * sizeMult);

        // intro1/intro3 共通: occlusion 有効ならオクルージョン判定を適用。
        // intro3 の場合は INITIAL_QUAT 固定なので「裏面 6 頂点」は常時非表示。
        if (occlusionEnabled) {
          camera.getWorldPosition(_cameraWorld);
          _rotMat.extractRotation(root.matrixWorld);
          const faceNormals = figureData.vertexFaceNormals;
          for (let i = 0; i < figureData.vertices.length; i++) {
            const [vx, vy, vz] = figureData.vertices[i];
            _vertWorld.set(vx, vy, vz).applyMatrix4(root.matrixWorld);
            _toCamera.subVectors(_cameraWorld, _vertWorld).normalize();
            const fs = faceNormals[i];
            let visible = false;
            for (let f = 0; f < 3; f++) {
              const n = fs[f];
              _normalWorld.set(n[0], n[1], n[2]).applyMatrix4(_rotMat);
              if (_toCamera.dot(_normalWorld) > -FACE_VISIBLE_EPSILON) {
                visible = true;
                break;
              }
            }
            const op = visible ? 1 : 0;
            dotOpacities[i] = op;
            dotFinalOpacities[i] = op;
            dots.setOpacity(i, op);
            const child = dots.group.children[i];
            if (child) child.visible = visible;
          }
        } else {
          // occlusion OFF: 全 24 点を表示。
          for (let i = 0; i < dotOpacities.length; i++) {
            dotOpacities[i] = 1;
            dotFinalOpacities[i] = 1;
            dots.setOpacity(i, 1);
            const child = dots.group.children[i];
            if (child) child.visible = true;
          }
        }
        // intro3 中の ghost 頂点 (= AB → AC 反射子): 通常はオクルージョン非表示
        // だが、6 点鏡開きの動きを見せるため強制的に可視化する。
        if (intro3Active) {
          for (let i = 0; i < dotOpacities.length; i++) {
            if (intro3IsGhost[i]) {
              dotOpacities[i] = 1;
              dotFinalOpacities[i] = 1;
              dots.setOpacity(i, 1);
              const child = dots.group.children[i];
              if (child) child.visible = true;
            }
          }
        }
        // intro4 中の corner 頂点 (8 つ): 4 つのパターンから選択 (active 1 つ、
        // 残り 3 つは過去パターンとしてコメントアウトで保管)。
        //
        // === パターン A: 回転中盤フェードイン (現 active) ===
        //   回転 = 3.0s → 3.8s。フェードイン = 3.2s → 3.6s (中盤 0.4s)
        if (intro4Active) {
          const tNorm = intro4Timer / INTRO4_DURATION;
          const fadeStartSec = 3.2; // 回転開始 (3.0s) から 0.2s 後
          const fadeEndSec = 3.6; // 回転終了 (3.8s) の 0.2s 前
          const fadeStart = fadeStartSec / INTRO4_DURATION;
          const fadeEnd = fadeEndSec / INTRO4_DURATION;
          let fadeFactor: number;
          if (tNorm < fadeStart) {
            fadeFactor = 0;
          } else if (tNorm < fadeEnd) {
            fadeFactor = (tNorm - fadeStart) / (fadeEnd - fadeStart);
          } else {
            fadeFactor = 1;
          }
          for (let i = 0; i < dotOpacities.length; i++) {
            if (intro4IsCorner[i]) {
              const base = dotOpacities[i];
              const finalOp = base * fadeFactor;
              dotOpacities[i] = finalOp;
              dotFinalOpacities[i] = finalOp;
              dots.setOpacity(i, finalOp);
              const child = dots.group.children[i];
              if (child) child.visible = finalOp > 0.001;
            }
          }
        }
        //
        // === パターン B: 回転開始直後 0.3s フェードイン ===
        //   フェードイン = 3.0s → 3.3s (回転開始から 0.3s)
        /*
        if (intro4Active) {
          const tNorm = intro4Timer / INTRO4_DURATION;
          const fadeStart = INTRO4_HOLD_FACE_ON_END;
          const fadeEnd = fadeStart + 0.3 / INTRO4_DURATION;
          let fadeFactor: number;
          if (tNorm < fadeStart) fadeFactor = 0;
          else if (tNorm < fadeEnd) fadeFactor = (tNorm - fadeStart) / (fadeEnd - fadeStart);
          else fadeFactor = 1;
          for (let i = 0; i < dotOpacities.length; i++) {
            if (intro4IsCorner[i]) {
              const finalOp = dotOpacities[i] * fadeFactor;
              dotOpacities[i] = finalOp;
              dotFinalOpacities[i] = finalOp;
              dots.setOpacity(i, finalOp);
              const child = dots.group.children[i];
              if (child) child.visible = finalOp > 0.001;
            }
          }
        }
        */
        //
        // === パターン C: 8→12 フェードイン (旧 phase 2、回転前) ===
        //   phase 2 中 (HOLD_RING_END → FACE_ON_END) で corner が fade in。
        //   この後 hold face-on で 12 dots 静止 → rotate。
        /*
        if (intro4Active) {
          const tNorm = intro4Timer / INTRO4_DURATION;
          let fadeFactor: number;
          if (tNorm < INTRO4_HOLD_RING_END) fadeFactor = 0;
          else if (tNorm < INTRO4_FACE_ON_END) {
            fadeFactor = (tNorm - INTRO4_HOLD_RING_END) /
              (INTRO4_FACE_ON_END - INTRO4_HOLD_RING_END);
          } else fadeFactor = 1;
          for (let i = 0; i < dotOpacities.length; i++) {
            if (intro4IsCorner[i]) {
              const finalOp = dotOpacities[i] * fadeFactor;
              dotOpacities[i] = finalOp;
              dotFinalOpacities[i] = finalOp;
              dots.setOpacity(i, finalOp);
              const child = dots.group.children[i];
              if (child) child.visible = finalOp > 0.001;
            }
          }
        }
        */
        //
        // === パターン D: 完全スキップ (corner intro4 中ずっと非表示) ===
        //   8 dots のみで回転、回転終了後 corner 表示 (occlusion 結果)。
        /*
        if (intro4Active) {
          if (intro4Timer / INTRO4_DURATION < INTRO4_ROTATE_END) {
            for (let i = 0; i < dotOpacities.length; i++) {
              if (intro4IsCorner[i]) {
                dotOpacities[i] = 0;
                dotFinalOpacities[i] = 0;
                dots.setOpacity(i, 0);
                const child = dots.group.children[i];
                if (child) child.visible = false;
              }
            }
          }
        }
        */
      }

      // intro 終了後、ラインを 0 → 通常 opacity へフェードイン。
      if (!introActive && linesIntroFadeT < 1) {
        linesIntroFadeT = Math.min(1, linesIntroFadeT + dt / LINE_FADE_DURATION);
        const fadedOp = linesModeOpacity * linesIntroFadeT;
        figure.setLinesOpacity(fadedOp);
        figure.setShowLines(fadedOp > 0.001);
        kaleidoscope.setLinesOpacity(fadedOp);
        kaleidoscope.setShowLines(fadedOp > 0.001);
      }

      if (gridMode) {
        renderGridPanes(dt);
      } else {
        renderer.setScissorTest(false);
        renderer.setViewport(0, 0, window.innerWidth, window.innerHeight);
        renderer.render(scene, camera);
        // メタボール: 頂点を画面へ投影し、メインシーンの上に blob 合成。
        // 重みは occlusion フェード (dotOpacities) なので裏の頂点は溶けて消える。
        // kaleidoscope ON のときはコピーの可視頂点も同じ場に投入し、隣接コピー
        // と溶けて繋がる「万華鏡 × メタボール」になる (上限は metaball.maxPoints)。
        if (metaballParams.enabled && !introActive) {
          const db = renderer.getDrawingBufferSize(_metaballDbSize);
          const vs = figureData.vertices;
          for (let i = 0; i < vs.length; i++) {
            _metaballWeights[i] = dotOpacities[i] * heartbeatFactors[i];
          }
          let n = 0;
          for (let i = 0; i < vs.length; i++) {
            if (_metaballWeights[i] < 0.01) continue; // 裏向きの頂点は除外
            if (n >= metaball.maxPoints) break;
            _metaballProj
              .set(vs[i][0], vs[i][1], vs[i][2])
              .applyMatrix4(root.matrixWorld)
              .project(camera);
            const px = (_metaballProj.x * 0.5 + 0.5) * db.x;
            const py = (_metaballProj.y * 0.5 + 0.5) * db.y;
            metaball.setPoint(n++, px, py, _metaballWeights[i]);
          }
          if (kaleidoParams.enabled && !gridMode) {
            n = kaleidoscope.appendMetaballPoints(
              metaball,
              n,
              camera,
              _metaballDbSize,
              _metaballWeights,
            );
          }
          metaball.setCount(n);
          metaball.render(renderer);
        }
      }
      // labelRenderer は両モードで render する。grid モードでは normal モード用の
      // CSS2D ドット / ラベルは opacity=0 で不可視 (updateVisibility 経由)。
      // GridDots の div も同じ label-layer 内なので、ここで display 切替えると一緒
      // に消えるためレイヤー自体は常に表示しておく。
      labelRenderer.render(scene, camera);

      // 動画書き出し中なら、レンダリング完了直後の canvas + DOM 状態を 1
      // フレームとしてキャプチャ + エンコード。経過時間が duration を超えたら
      // finalize → ダウンロード。
      captureFrameIfRecording();
    };

    // === 後始末 ===
    this.teardown = (): void => {
      // 録画中なら強制的に終了させる (途中までの mp4/webm は出ない)。
      recording = null;
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", syncRenderersSize);
      window.removeEventListener("resize", applyAutoPointSize);
      mobileMql.removeEventListener("change", onMobileChange);
      const cEl = renderer.domElement;
      cEl.removeEventListener("touchstart", onTouchStart);
      cEl.removeEventListener("touchmove", onTouchMove);
      cEl.removeEventListener("touchend", onTouchEnd);
      cEl.removeEventListener("touchcancel", onTouchEnd);
      controls.dispose();
      gui.destroy();
      kaleidoscope.dispose();
      metaball.dispose();
      sceneCtx.dispose();
      container.replaceChildren();
    };
  }

  unmount(): void {
    this.teardown?.();
    this.teardown = null;
    this.updateFn = null;
  }

  update(dt: number): void {
    this.updateFn?.(dt);
  }
}
